import { useMediaQuery } from "@chakra-ui/react";
import { TransactionResponse } from "@ethersproject/providers";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Connector,
  useAccount,
  useConnect,
  useDisconnect,
  usePublicClient,
  useSendTransaction,
  useSwitchChain,
  useWalletClient,
  useWriteContract,
} from "wagmi";

import { Text, useToast } from "@chakra-ui/react";

import AccountModal from "../components/AccountModal";
import WalletConnectModal from "../components/WalletConnectModal";
import {
  APPROVAL_AMOUNT,
  toFixedWithoutRounding,
  TXN_STATUSES,
  TXN_TYPES,
} from "../constants/common";

import { MODAL_TYPES } from "../constants/modal";
import { EVENTS } from "../constants/reduxEvents";

import DepositModalV2, {
  TransactionType,
} from "@components/DepositModal/DepositModalV2";
import StaderLedgerTxModal from "@components/StaderLedgerTxModal/StaderLedgerTxModal";
import {
  ETH,
  ETHX,
  RSETH,
  stableVaultContractConfig,
  STETH,
  WRSETH,
  USDT,
} from "@config/contractsConfig";
import {
  CLAIM_FAILED,
  CLAIM_SUCCESSFUL,
  MAX_STAKE,
  MAX_UNSTAKE,
  STABLE_VAULT,
  CRYPTANIUM_VAULT,
  STAKE_FAILED,
  STAKE_SUCCESSFUL,
  TOKEN_APPROVE_FAILED,
  TOKEN_APPROVE_SUCCESSFUL,
  UNSTAKE_FAILED,
  UNSTAKE_SUCCESSFUL,
  VAULT_CLAIM_FAILED,
  VAULT_CLAIM_SUCCESSFUL,
  VAULT_DEPOSIT_FAILED,
  VAULT_DEPOSIT_SUCCESSFUL,
  VAULT_WITHDRAW_FAILED,
  VAULT_WITHDRAW_SUCCESSFUL,
  VIEW_ADDRESS_ON_SCANNER,
  WALLET_CONNECTED_SUCCESSFULLY,
  YIELD_VAULT,
} from "@constants/analytics";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";
import useAutoConnect from "@hooks/useAutoConnect";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useExchangeRate from "@hooks/useExchangeRate";
import useHgEthExchangeRate from "@hooks/useHgEthExchangeRate";
import useTokenBalance from "@hooks/useTokenBalance";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  formatUnits,
  maxUint256,
  parseEther,
  parseUnits,
  sha256,
  TransactionReceipt,
} from "viem";
import {
  arbitrum,
  avalanche,
  base,
  plasma,
  sonic,
  tac,
  // mainnet,
  xLayer,
} from "viem/chains";
import TransactionModal from "../components/TransactionModal";
import WalletMessageModal from "../components/WalletMessageModal";
import {
  clearUserData,
  store,
  updateModalData,
  updateStakeData,
  updateUserData,
  updateWalletData,
} from "../store";
import {
  emitReduxEvent,
  formatNumber,
  isErrorRejectedByUser,
  trackEvent,
} from "../../utils";
import useCryptaniumVaultExchangeRate from "@hooks/useCryptaniumVaultExchangeRate";
// import { wagmiConfig } from "./WagmiProvider";
// import axios from "axios";

// import { ethers } from "ethers";
// import useUnstakeInfo from "@hooks/useUnstakeInfo";
// import useBufferPool from "@hooks/useBufferPool";

export interface TxnErrorType {
  hasUserDenied: boolean;
  promptError: string;
}

export interface TxnType {
  original: any;
  hash: string;
  status: string;
  amount: string | null;
  token: string;
  type: string;
  isVault?: string;
}

const initialTxnError = {
  hasUserDenied: false,
  promptError: "",
};

interface StaderLedgerTxnType {
  hash: string;
  status: string;
  activeIndex: number;
  error?: string;
  hasUserDenied?: boolean;
}

const initialStaderLedgerTxn = {
  hash: "",
  status: "",
  activeIndex: -1,
};

const initialTxn = {
  original: undefined,
  hash: "",
  status: "",
  amount: null,
  token: "",
  type: "",
};

interface TransactionInterface {
  transaction: {
    from: string;
    to: string;
    amountIn: string;
    amountOut: string;
  };
  status: TransactionType;
  type: TransactionType;
  receipt: TransactionReceipt | null;
  inProgress: boolean;
  visible: boolean;
  complete: boolean;
  error: {
    approval: TxnErrorType | null;
    deposit: TxnErrorType | null;
  };
  flags: {
    isUnstake: boolean;
    isVault: boolean;
    isYieldVault?: boolean;
    isStableVault?: boolean;
    isCryptaniumVault?: boolean;
    isClaim?: boolean;
  };
  nextAction?: () => void;
}

const initialTransaction = {
  transaction: {
    from: "",
    to: "",
    amountIn: "",
    amountOut: "",
  },
  status: TransactionType.APPROVAL,
  type: TransactionType.APPROVAL,
  receipt: null,
  inProgress: false,
  error: {
    approval: null,
    deposit: null,
  },
  flags: {
    isUnstake: false,
    isVault: false,
    isClaim: false,
  },
  visible: false,
  complete: false,
};

interface ServicesProps {
  children: ReactNode;
}

const Services = ({ children }: ServicesProps) => {
  const transactionWaitRef = useRef(false);
  const queryClient = useQueryClient();

  const { modalType, isMobileView, errorMessage } = useSelector(
    (state: any) => state.modal
  );
  const { emitter } = useSelector((state: any) => state.event);
  const {
    selectedERC20,
    selectedERC20Vault,
    selectedERC20YieldVault,
    selectedERC20StableVault,
    selectedERC20Cryptanium,
    referralId,
  } = useSelector((state: any) => state.user);
  const { nativeTokenBalance, tokensBalances } = useTokenBalance();

  const tokenAmount =
    selectedERC20 === ETH
      ? nativeTokenBalance
      : tokensBalances[selectedERC20] || 0;

  useAutoConnect();

  const [txnError, setTxnError] = useState<TxnErrorType>(initialTxnError);
  const [txnLoader, setTxnLoader] = useState(false);
  const [txnDetail, setTxnDetail] = useState<TxnType>(initialTxn);
  const [staderLedgerTxn, setStaderLedgerTxn] = useState<StaderLedgerTxnType>(
    initialStaderLedgerTxn
  );

  const [transaction, setTransaction] =
    useState<TransactionInterface>(initialTransaction);

  const transactionParams = useRef<any>(null);

  const { exchangeRate } = useExchangeRate();
  const { exchangeRate: vaultExchangeRate } = useExchangeRate("vault");
  const { exchangeRate: yieldVaultExchangeRate } =
    useExchangeRate("yieldvault");
  const conversionsDataAgEth = useAgEthExchangeRate();
  const conversionsDataHgEth = useHgEthExchangeRate();
  const conversionsDataXgUSD = useCryptaniumVaultExchangeRate();

  const [isMobileOrTablet] = useMediaQuery("(max-width: 768px)");

  const toast = useToast();
  const { chain: wagmiChain } = useAccount();

  const { data: signer } = useWalletClient();
  const { switchChain, chains } = useSwitchChain();
  const { isConnected, address, connector: activeConnector } = useAccount();
  const { chain, isEthereumChain } = useDappChain();
  const publicClient = usePublicClient();
  const {
    config,
    isRestakeSupported,
    isWithdrawSupported,
    vaultsContractConfig,
    yieldVaultContractConfig,
    cryptaniumVaultContractConfig,
  } = useDappConfig();

  const { writeContract } = useWriteContract();
  const { sendTransaction } = useSendTransaction();

  useTokenBalance();

  const dispatch = useDispatch();

  const { disconnect } = useDisconnect();

  const { connectModalOpen } = useConnectModal();

  useEffect(() => {
    if (connectModalOpen) {
      setTimeout(() => {
        dispatch(
          updateModalData({
            modalType: "",
            isMobileView: false,
            errorMessage: "",
          })
        );
      }, 200);
    }
  }, [connectModalOpen, dispatch]);

  const handleCloseModal = useCallback(() => {
    dispatch(
      updateModalData({
        modalType: "",
        isMobileView: false,
        errorMessage: "",
      })
    );
  }, [dispatch]);

  const { connect } = useConnect();

  const onWalletConnect = useCallback(
    (connector: Connector) => {
      connect(
        { connector },
        {
          onError(error) {
            dispatch(
              updateModalData({
                modalType: MODAL_TYPES.WALLET_CONNECT_MESSAGE_MODAL,
                isMobileView: false,
                errorMessage: error.message,
              })
            );
          },
          onSuccess() {
            trackEvent(WALLET_CONNECTED_SUCCESSFULLY);
            dispatch(
              updateModalData({
                modalType: MODAL_TYPES.WALLET_CONNECT_MESSAGE_MODAL,
                isMobileView: false,
              })
            );
          },
        }
      );
    },
    [connect]
  );

  const onCloseAccountModal = useCallback(
    (isWalletDisconnect: boolean) => {
      handleCloseModal();
      if (isWalletDisconnect) {
        disconnect();
        dispatch(clearUserData());
      }
    },
    [disconnect, handleCloseModal, dispatch]
  );

  const initiateNewTxn = useCallback(() => {
    transactionWaitRef.current = false;
    setTxnError(initialTxnError);
    setTxnLoader(true);
    setTxnDetail(initialTxn);
  }, []);

  const updateTransactionDetails = useCallback(
    (
      txn: TransactionResponse,
      type: string,
      amount: string | null,
      token: string
      // id: number | null = null
    ) => {
      setTxnDetail({
        original: txn,
        hash: txn.hash,
        status: TXN_STATUSES.PENDING,
        amount,
        token,
        type,
        // id,
      });
    },
    []
  );

  const resetTxns = useCallback(() => {
    setTxnDetail(initialTxn);
    setTxnLoader(false);
  }, []);

  const handleErrror = useCallback(
    (error: string, hasUserDenied = false, custmError?: string) => {
      setTxnError({
        hasUserDenied: hasUserDenied,
        promptError: error,
      });
      if (!txnLoader) {
        let errorMsg = hasUserDenied ? "User rejected request" : error;
        if (custmError) {
          errorMsg = custmError;
        }
        toast({
          description: (
            <Text fontWeight={600} fontSize="16px">
              {errorMsg}
            </Text>
          ),
          status: "error",
          // icon: <Icon Icon={ErrorToast} width="24px" height="24px" />,
          duration: 5000,
          position: "top",
          isClosable: false,
        });
      }
    },
    [toast, txnLoader]
  );

  const getAgEthExchange = () => {
    const rsETHRate =
      selectedERC20Vault === RSETH || selectedERC20Vault === WRSETH
        ? 1
        : Number(vaultExchangeRate);
    const res = Number(conversionsDataAgEth?.toERC20) * rsETHRate;
    return res > 0 ? res : 1;
  };

  const getHgEthExchange = () => {
    const rsETHRate =
      selectedERC20YieldVault === RSETH ? 1 : Number(yieldVaultExchangeRate);
    const res = Number(conversionsDataHgEth?.toERC20) * rsETHRate;
    return res > 0 ? res : 1;
  };

  const getXgUSDExchangeRate = () => {
    const xgUSDRate =
      selectedERC20Cryptanium === USDT
        ? 1
        : Number(conversionsDataXgUSD?.toERC20);
    const res = Number(conversionsDataXgUSD?.toERC20) * xgUSDRate;
    return res > 0 ? res : 1;
  };
  const handleVaultApprove = ({ amount = APPROVAL_AMOUNT }) => {
    const exchangeRate = getAgEthExchange();
    if (address && signer) {
      setTransaction({
        ...initialTransaction,
        flags: {
          isVault: true,
          isUnstake: false,
        },

        visible: true,
        inProgress: true,

        transaction: {
          from: selectedERC20Vault,
          to: vaultsContractConfig[chain.id].xToken.symbol,
          amountIn: amount || "0",
          amountOut: toFixedWithoutRounding(
            (Number(amount) / exchangeRate).toString(),
            4
          ),
        },
      });

      let token = config.tokens[selectedERC20Vault];
      if (selectedERC20Vault === config.xtoken.symbol) {
        token = {
          ...config.xtoken,
          tags: [],
        };
      }

      const vaultConfig = isEthereumChain
        ? vaultsContractConfig[chain.id].rsETHAdapterPool
        : vaultsContractConfig[chain.id].interactionPool;

      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig?.address, parseEther(amount)],
        },
        {
          onError: (error: any) => {
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;
            const userRejected = isErrorRejectedByUser(error);
            handleErrror(errorMsg, userRejected, "Approval denied");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: parseFloat(amount),
            });
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceed to Deposit
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });
                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token,
                    token_amount: parseFloat(amount),
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleVaultDeposit({ amount, referralId });
                  }, 2000);
                }
              });
          },
        }
      );
    }
  };

  const handleYieldVaultApprove = ({ amount = APPROVAL_AMOUNT }) => {
    // const amount = APPROVAL_AMOUNT;
    const exchangeRate = getHgEthExchange();
    // initiateNewTxn();

    if (address && signer) {
      setTransaction({
        ...initialTransaction,
        flags: {
          isVault: true,
          isYieldVault: true,
          isUnstake: false,
        },

        visible: true,
        inProgress: true,

        transaction: {
          from: selectedERC20YieldVault,
          to: yieldVaultContractConfig[chain.id].xToken.symbol,
          amountIn: amount || "0",
          amountOut: toFixedWithoutRounding(
            (Number(amount) / exchangeRate).toString(),
            4
          ),
        },
      });
      let token = config.tokens[selectedERC20YieldVault];

      if (selectedERC20YieldVault === config.xtoken.symbol) {
        token = {
          ...config.xtoken,
          tags: [],
        };
      }

      const vaultConfig = yieldVaultContractConfig[chain.id].gainAdapterETH;

      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig?.address, parseEther(amount)],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;
            handleErrror(errorMsg, userRejected, "Approval denied");
            // dispatch(
            //   updateStakeData({
            //     isTransactionProcessing: false,
            //   })
            // );
            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: parseFloat(amount),
            });
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceed to Deposit
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });
                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token,
                    token_amount: parseFloat(amount),
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleYieldVaultDeposit({ amount, referralId });
                  }, 2000);
                }
              });
          },
        }
      );
    }
  };
  // if (address && signer) {
  //   dispatch(updateStakeData({ isTransactionProcessing: true }));

  //   let token = config.tokens[selectedERC20YieldVault];

  //   if (selectedERC20YieldVault === config.xtoken.symbol) {
  //     token = {
  //       ...config.xtoken,
  //       tags: [],
  //     };
  //   }

  //   const vaultConfig = yieldVaultContractConfig[chain.id].gainAdapterETH;

  //   writeContract(
  //     {
  //       ...token,
  //       functionName: "approve",
  //       args: [vaultConfig?.address, parseEther(amount)],
  //     },
  //     {
  //       onError: (error: any) => {
  //         const userRejected = isErrorRejectedByUser(error);

  //         const errorMessage =
  //           error?.shortMessage || error?.error || error?.message;

  //         handleErrror(errorMessage, userRejected, "Approval denied");
  //         dispatch(
  //           updateStakeData({
  //             isTransactionProcessing: false,
  //           })
  //         );
  //         //  trackEvent(TOKEN_APPROVE_FAILED, {
  //         //    reason: isErrorRejectedByUser(error)
  //         //      ? "user rejected"
  //         //      : error?.error || error?.message,
  //         //  });
  //         resetTxns();
  //       },
  //       onSuccess: (hash) => {
  //         const txDetails = {
  //           hash,
  //           wait: () => publicClient?.waitForTransactionReceipt({ hash }),
  //           isVault: true,
  //         };
  //         updateTransactionDetails(
  //           txDetails as any,
  //           TXN_TYPES.ERC20_APPROVE,
  //           null,
  //           token.symbol
  //         );
  //       },
  //     }
  //   );
  // }

  const handleYieldVaultXTokenApprove = ({ amount = APPROVAL_AMOUNT }) => {
    const exchangeRate = getHgEthExchange();
    if (address && signer) {
      setTransaction({
        ...initialTransaction,
        flags: {
          isVault: true,
          isYieldVault: true,
          isUnstake: true,
        },

        visible: true,
        inProgress: true,

        transaction: {
          from: yieldVaultContractConfig[chain.id].xToken.symbol,
          to: RSETH,
          amountIn: amount || "0",
          amountOut: toFixedWithoutRounding(
            (Number(amount) * exchangeRate).toString(),
            4
          ),
        },
      });

      const vaultConfig = yieldVaultContractConfig[chain.id];
      let token = vaultConfig.xToken;

      writeContract(
        {
          ...vaultConfig?.interactionPool,
          functionName: "approve",
          args: [vaultConfig?.gainAdapterETH?.address, parseEther(amount)],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;
            handleErrror(errorMsg, userRejected, "Approval denied");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: parseFloat(amount),
            });
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceed to Withdraw
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });
                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token,
                    token_amount: parseFloat(amount),
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleYieldVaultWithdraw({ amount, referralId });
                  }, 2000);
                }
              });
          },
        }
      );
    }
  };

  const handleStableVaultDeposit = ({ amount, amountOut }: any) => {
    if (!address) {
      console.error("No address available for deposit");
      return;
    }
    const { user } = store.getState();
    const { referralId } = user;

    if (address) {
      setTransaction((prevTransaction) => ({
        ...prevTransaction,
        flags: {
          isVault: true,
          isUnstake: false,
          isStableVault: true,
        },
        visible: true,
        inProgress: true,
        status: TransactionType.DEPOSIT,
        type: TransactionType.APPROVAL,
        transaction: {
          from: selectedERC20StableVault,
          to: stableVaultContractConfig[chain.id].xToken.symbol,
          amountIn: amount || "0",
          amountOut: amountOut || amount || "0",
        },
        error: {
          approval: null,
          deposit: null,
        },
      }));

      let token =
        stableVaultContractConfig[chain.id]?.tokens?.[selectedERC20StableVault];

      if (!token) {
        token = config.tokens[selectedERC20StableVault];
      }

      const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;

      if (!token || !vaultConfig) {
        setTransaction((prevTransaction) => ({
          ...prevTransaction,
          visible: true,
          inProgress: false,
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          error: {
            approval: null,
            deposit: {
              hasUserDenied: false,
              promptError: "Token or vault configuration not found",
            },
          },
        }));
        return;
      }

      const depositArgs = [
        token.address,
        parseUnits(amount, token.decimals),
        address,
      ];

      //in case referral id is available
      const depositArgsWithReferralId = [
        token.address,
        parseUnits(amount, token.decimals),
        address,
        sha256(referralId),
      ];

      let depositConfig = {
        ...vaultConfig,
        functionName: "depositAsset",
        args: depositArgs,
      };

      //in case referral id is available
      if (referralId) {
        depositConfig = {
          ...vaultConfig,
          functionName: "depositAssetWithReferral",
          args: depositArgsWithReferralId,
        };
      }

      writeContract(depositConfig, {
        onError: (error: any) => {
          console.error("Stable vault deposit error:", error);
          const userRejected = isErrorRejectedByUser(error);
          const errorMsg =
            error?.shortMessage || error?.error || error?.message;

          handleErrror(errorMsg, userRejected, "Deposit failed");

          trackEvent("STABLE_VAULT_DEPOSIT_FAILED", {
            errorReason: userRejected ? "user rejected" : errorMsg,
            token_symbol: selectedERC20StableVault,
            token_amount: parseFloat(amount),
          });

          setTransaction((prevTransaction) => ({
            ...prevTransaction,
            inProgress: false,
            type: TransactionType.DEPOSIT,
            status: TransactionType.DEPOSIT,
            error: {
              approval: null,
              deposit: {
                hasUserDenied: userRejected,
                promptError: errorMsg,
              },
            },
          }));

          trackEvent(STABLE_VAULT.DEPOSIT_FAILED, {
            asset_in: selectedERC20StableVault,
            asset_out: stableVaultContractConfig[chain.id].xToken.symbol,
            asset_in_amt: parseFloat(amount),
            asset_out_amt: parseFloat(amountOut),
            errorReason: userRejected ? "user rejected" : errorMsg,
          });
        },
        onSuccess: (hash) => {
          publicClient
            ?.waitForTransactionReceipt({ hash })
            .then((confirmation) => {
              if (confirmation.blockHash) {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Deposit successful!
                    </Text>
                  ),
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });

                setTransaction((prevTransaction) => ({
                  ...prevTransaction,
                  visible: true,
                  inProgress: false,
                  complete: true,
                  receipt: confirmation,
                }));

                queryClient?.invalidateQueries({
                  queryKey: ["tokenBalance"],
                });
                queryClient?.invalidateQueries({
                  queryKey: ["erc20Allowance"],
                });

                trackEvent(STABLE_VAULT.DEPOSIT_SUCCESSFUL, {
                  asset_in: selectedERC20StableVault,
                  asset_out: stableVaultContractConfig[chain.id].xToken.symbol,
                  asset_in_amt: parseFloat(amount),
                  asset_out_amt: parseFloat(amountOut),
                });

                dispatch(
                  updateStakeData({
                    isTransactionProcessing: false,
                  })
                );

                dispatch(updateUserData({ stableFormResetNonce: Date.now() }));
              }
            });
        },
      });
    }
  };

  const handleCryptaniumVaultDeposit = async (data: any) => {
    const { amount } = data;

    const vaultConfig = cryptaniumVaultContractConfig[chain.id] || {};

    if (address && signer) {
      setTransaction((transaction) => ({
        ...transaction,
        visible: true,
        status: TransactionType.DEPOSIT,
        inProgress: true,
        flags: {
          ...initialTransaction.flags,
          isVault: true,
          isCryptaniumVault: true,
          isClaim: false,
        },
        error: initialTransaction.error,
      }));
      let params: any = {};

      const token =
        selectedERC20Cryptanium === vaultConfig?.xToken?.symbol
          ? vaultConfig?.xToken
          : vaultConfig?.tokens[selectedERC20Cryptanium];

      const amountOut = transaction?.transaction?.amountOut
        ? parseUnits(transaction.transaction.amountOut, token.decimals)
        : 0;

      params = {
        functionName: "deposit",
        args: [parseUnits(amount, token.decimals), address, amountOut],
      };

      try {
        writeContract(
          {
            ...vaultConfig.interactionPool,
            ...params,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);
              const errorMessage =
                error?.shortMessage || error?.error || error?.message;
              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(CRYPTANIUM_VAULT.DEPOSIT_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Deposit Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(CRYPTANIUM_VAULT.DEPOSIT_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
      }
    }
  };

  const handleStableVaultXTokenApprove = ({
    amount = APPROVAL_AMOUNT,
    amountOut,
    outputToken = "USDT",
  }: any) => {
    if (!stableVaultContractConfig[chain.id]) {
      toast({
        description: "Stable vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    if (!address || !signer) return;

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: true,
        isStableVault: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: stableVaultContractConfig[chain.id].xToken.symbol,
        to: outputToken,
        amountIn: amount || "0",
        amountOut: amountOut || amount || "0",
      },
    });

    const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;
    let token = stableVaultContractConfig[chain.id]?.xToken;

    if (!token || !vaultConfig) {
      console.error(
        "Token or vault config not found for stable vault xtoken approve"
      );
      return;
    }

    const checkAllowanceAndApprove = async () => {
      try {
        const currentAllowance = await publicClient?.readContract({
          address: token.address as `0x${string}`,
          abi: token.abi,
          functionName: "allowance",
          args: [address as `0x${string}`, vaultConfig.address],
        });

        const currentAllowanceFormatted = currentAllowance
          ? formatUnits(currentAllowance as bigint, token.decimals)
          : "0";

        const requestedAmount = parseFloat(amount);
        const currentAmount = parseFloat(currentAllowanceFormatted);

        const missingAmount = Math.max(0, requestedAmount - currentAmount);

        if (missingAmount <= 0) {
          toast({
            description: (
              <Text fontWeight={600} fontSize="16px">
                Sufficient allowance available, proceeding to withdraw
              </Text>
            ),
            status: "success",
            duration: 3000,
            position: "top",
            isClosable: false,
          });

          setTransaction((transaction) => ({
            ...transaction,
            receipt: null,
            status: TransactionType.DEPOSIT,
            inProgress: false,
          }));

          setTimeout(() => {
            handleStableVaultWithdraw({ amount, amountOut, outputToken });
          }, 1000);
          return;
        }

        const approvalAmount = maxUint256;
        const displayAmount = parseFloat(APPROVAL_AMOUNT);

        approveToken(approvalAmount, displayAmount);
      } catch (error) {
        console.error("Error checking allowance:", error);
        const fullApprovalAmount = maxUint256;
        approveToken(fullApprovalAmount, parseFloat(APPROVAL_AMOUNT));
      }
    };

    const approveToken = (approvalAmount: bigint, displayAmount: number) => {
      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig.address, approvalAmount],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Approval failed");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: displayAmount,
            });

            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceeding to withdraw
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token.symbol,
                    token_amount: displayAmount,
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleStableVaultWithdraw({
                      amount,
                      amountOut,
                      outputToken,
                    });
                  }, 2000);
                }
              });
          },
        }
      );
    };

    checkAllowanceAndApprove();
  };

  const handleCryptaniumVaultXTokenApprove = ({
    amount = APPROVAL_AMOUNT,
    outputToken = "USDT",
  }) => {
    if (!cryptaniumVaultContractConfig[chain.id]) {
      toast({
        description: "Cryptanium vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    if (!address || !signer) return;

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: true,
        isCryptaniumVault: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: cryptaniumVaultContractConfig[chain.id].xToken.symbol,
        to: outputToken,
        amountIn: amount || "0",
        amountOut: amount || "0", // For cryptanium vault, 1:1 ratio typically
      },
    });

    const vaultConfig =
      cryptaniumVaultContractConfig[chain.id]?.interactionPool;
    let token = cryptaniumVaultContractConfig[chain.id]?.xToken;

    if (!token || !vaultConfig) {
      console.error(
        "Token or vault config not found for cryptanium vault xtoken approve"
      );
      return;
    }

    const checkAllowanceAndApprove = async () => {
      try {
        const currentAllowance = await publicClient?.readContract({
          address: token.address as `0x${string}`,
          abi: token.abi,
          functionName: "allowance",
          args: [address as `0x${string}`, vaultConfig.address],
        });

        const currentAllowanceFormatted = currentAllowance
          ? formatUnits(currentAllowance as bigint, token.decimals)
          : "0";

        const requestedAmount = parseFloat(amount);
        const currentAmount = parseFloat(currentAllowanceFormatted);

        const missingAmount = Math.max(0, requestedAmount - currentAmount);

        if (missingAmount <= 0) {
          toast({
            description: (
              <Text fontWeight={600} fontSize="16px">
                Sufficient allowance available, proceeding to withdraw
              </Text>
            ),
            status: "success",
            duration: 3000,
            position: "top",
            isClosable: false,
          });

          setTransaction((transaction) => ({
            ...transaction,
            receipt: null,
            status: TransactionType.DEPOSIT,
            inProgress: false,
          }));

          setTimeout(() => {
            handleCryptaniumVaultWithdraw({ amount, outputToken });
          }, 1000);
          return;
        }

        const approvalAmount = parseUnits(APPROVAL_AMOUNT, token.decimals);
        const displayAmount = parseFloat(APPROVAL_AMOUNT);

        approveToken(approvalAmount, displayAmount);
      } catch (error) {
        console.error("Error checking allowance:", error);
        const fullApprovalAmount = parseUnits(APPROVAL_AMOUNT, token.decimals);
        approveToken(fullApprovalAmount, parseFloat(APPROVAL_AMOUNT));
      }
    };

    const approveToken = (approvalAmount: bigint, displayAmount: number) => {
      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig.address, approvalAmount],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Approval failed");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: displayAmount,
            });

            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceeding to withdraw
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token.symbol,
                    token_amount: displayAmount,
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleCryptaniumVaultWithdraw({ amount, outputToken });
                  }, 2000);
                }
              });
          },
        }
      );
    };

    // Execute the allowance check and smart approval
    checkAllowanceAndApprove();
  };

  const handleStableVaultWithdraw = ({
    amount,
    amountOut,
    outputToken = "USDT",
  }: any) => {
    if (!address) {
      console.error("No address available for withdraw");
      return;
    }

    if (!stableVaultContractConfig[chain.id]) {
      setTransaction((prevTransaction) => ({
        ...prevTransaction,
        visible: true,
        inProgress: false,
        status: TransactionType.DEPOSIT,
        type: TransactionType.DEPOSIT,
        error: {
          approval: null,
          deposit: {
            hasUserDenied: false,
            promptError: "Stable vault not supported on this network",
          },
        },
      }));
      return;
    }

    if (address) {
      setTransaction((prevTransaction) => ({
        ...prevTransaction,
        flags: {
          isVault: true,
          isUnstake: true,
          isStableVault: true,
        },
        visible: true,
        inProgress: true,
        status: TransactionType.DEPOSIT,
        type: TransactionType.APPROVAL,
        transaction: {
          from: stableVaultContractConfig[chain.id].xToken.symbol,
          to: outputToken,
          amountIn: amount || "0",
          amountOut: amountOut || amount || "0",
        },
        error: {
          approval: null,
          deposit: null,
        },
      }));

      const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;
      let token = stableVaultContractConfig[chain.id]?.xToken;

      if (!token || !vaultConfig) {
        setTransaction((prevTransaction) => ({
          ...prevTransaction,
          visible: true,
          inProgress: false,
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          error: {
            approval: null,
            deposit: {
              hasUserDenied: false,
              promptError: "Token or vault configuration not found",
            },
          },
        }));
        return;
      }

      const outputTokenConfig =
        stableVaultContractConfig[chain.id]?.tokens?.[outputToken] ||
        config.tokens[outputToken];

      if (!outputTokenConfig) {
        setTransaction((prevTransaction) => ({
          ...prevTransaction,
          visible: true,
          inProgress: false,
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          error: {
            approval: null,
            deposit: {
              hasUserDenied: false,
              promptError: `${outputToken} token configuration not found`,
            },
          },
        }));
        return;
      }

      const withdrawArgs = [
        outputTokenConfig.address,
        parseUnits(amount, token.decimals),
        address,
        address,
      ];

      writeContract(
        {
          ...vaultConfig,
          functionName: "requestRedeemOfAsset",
          args: withdrawArgs,
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Withdraw failed");

            trackEvent("STABLE_VAULT_WITHDRAW_FAILED", {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: stableVaultContractConfig[chain.id].xToken.symbol,
              token_amount: parseFloat(amount),
            });

            setTransaction((prevTransaction) => ({
              ...prevTransaction,
              inProgress: false,
              type: TransactionType.DEPOSIT,
              status: TransactionType.DEPOSIT,
              error: {
                approval: null,
                deposit: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
              },
            }));

            trackEvent(STABLE_VAULT.WITHDRAW_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: stableVaultContractConfig[chain.id].xToken.symbol,
              token_amount: parseFloat(amount),
            });
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Withdraw successful!
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((prevTransaction) => ({
                    ...prevTransaction,
                    visible: true,
                    inProgress: false,
                    complete: true,
                    receipt: confirmation,
                  }));

                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });

                  trackEvent(STABLE_VAULT.WITHDRAW_SUCCESSFUL, {
                    asset_in: stableVaultContractConfig[chain.id].xToken.symbol,
                    asset_out: outputToken,
                    asset_in_amt: parseFloat(amount),
                    asset_out_amt: parseFloat(amountOut),
                  });

                  dispatch(
                    updateStakeData({
                      isTransactionProcessing: false,
                    })
                  );

                  dispatch(
                    updateUserData({ stableFormResetNonce: Date.now() })
                  );
                }
              });
          },
        }
      );
    }
  };

  const handleCryptaniumVaultWithdraw = ({
    amount,
    outputToken = "USDT",
  }: any) => {
    if (!address) {
      console.error("No address available for withdraw");
      return;
    }

    if (!cryptaniumVaultContractConfig[chain.id]) {
      setTransaction((prevTransaction) => ({
        ...prevTransaction,
        visible: true,
        inProgress: false,
        status: TransactionType.DEPOSIT,
        type: TransactionType.DEPOSIT,
        error: {
          approval: null,
          deposit: {
            hasUserDenied: false,
            promptError: "Cryptanium vault not supported on this network",
          },
        },
      }));
      return;
    }

    if (address) {
      setTransaction((prevTransaction) => ({
        ...prevTransaction,
        flags: {
          isVault: true,
          isUnstake: true,
          isCryptaniumVault: true,
        },
        visible: true,
        inProgress: true,
        status: TransactionType.DEPOSIT,
        type: TransactionType.APPROVAL,
        transaction: {
          from: cryptaniumVaultContractConfig[chain.id].xToken.symbol,
          to: outputToken,
          amountIn: amount || "0",
          amountOut: amount || "0",
        },
        error: {
          approval: null,
          deposit: null,
        },
      }));

      const vaultConfig =
        cryptaniumVaultContractConfig[chain.id]?.interactionPool;
      let token = cryptaniumVaultContractConfig[chain.id]?.xToken;

      if (!token || !vaultConfig) {
        setTransaction((prevTransaction) => ({
          ...prevTransaction,
          visible: true,
          inProgress: false,
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          error: {
            approval: null,
            deposit: {
              hasUserDenied: false,
              promptError: "Token or vault configuration not found",
            },
          },
        }));
        return;
      }

      const outputTokenConfig =
        cryptaniumVaultContractConfig[chain.id]?.tokens?.[outputToken] ||
        config.tokens[outputToken];

      if (!outputTokenConfig) {
        setTransaction((prevTransaction) => ({
          ...prevTransaction,
          visible: true,
          inProgress: false,
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          error: {
            approval: null,
            deposit: {
              hasUserDenied: false,
              promptError: `${outputToken} token configuration not found`,
            },
          },
        }));
        return;
      }

      const withdrawArgs = [
        outputTokenConfig.address,
        parseUnits(amount, token.decimals),
        address,
        address,
      ];

      writeContract(
        {
          ...vaultConfig,
          functionName: "requestRedeemOfAsset",
          args: withdrawArgs,
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Withdraw failed");

            trackEvent("CRYPTANIUM_VAULT_WITHDRAW_FAILED", {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol:
                cryptaniumVaultContractConfig[chain.id].xToken.symbol,
              token_amount: parseFloat(amount),
            });

            setTransaction((prevTransaction) => ({
              ...prevTransaction,
              inProgress: false,
              type: TransactionType.DEPOSIT,
              status: TransactionType.DEPOSIT,
              error: {
                approval: null,
                deposit: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Withdraw successful!
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((prevTransaction) => ({
                    ...prevTransaction,
                    visible: true,
                    inProgress: false,
                    complete: true,
                    receipt: confirmation,
                  }));

                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });

                  dispatch(
                    updateStakeData({
                      isTransactionProcessing: false,
                    })
                  );

                  dispatch(
                    updateUserData({ cryptaniumFormResetNonce: Date.now() })
                  );
                }
              });
          },
        }
      );
    }
  };

  const handleStableVaultClaim = ({ receiver, tokenId, amount }: any) => {
    if (!address) {
      console.error("No address available for claim");
      return;
    }

    if (!stableVaultContractConfig[chain.id]) {
      toast({
        description: "Stable vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;

    if (!vaultConfig) {
      toast({
        description: "Vault configuration not found",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    // Set loading state
    dispatch(updateStakeData({ isTransactionProcessing: true }));

    trackEvent(STABLE_VAULT.CLAIM_INITIATED, {
      token_id: tokenId,
      amount: parseFloat(formatUnits(amount || 0n, 18)),
    });

    writeContract(
      {
        ...vaultConfig,
        functionName: "fulfillRedeem",
        args: [receiver],
      },
      {
        onError: (error: any) => {
          const userRejected = isErrorRejectedByUser(error);
          const errorMsg =
            error?.shortMessage || error?.error || error?.message;

          handleErrror(errorMsg, userRejected, "Claim failed");

          trackEvent(STABLE_VAULT.CLAIM_FAILED, {
            errorReason: userRejected ? "user rejected" : errorMsg,
            token_id: tokenId,
            amount: parseFloat(formatUnits(amount || 0n, 18)),
          });

          dispatch(
            updateStakeData({
              isTransactionProcessing: false,
            })
          );
        },
        onSuccess: (hash) => {
          publicClient
            ?.waitForTransactionReceipt({ hash })
            .then((confirmation) => {
              if (confirmation.blockHash) {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Claim successful!
                    </Text>
                  ),
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });

                trackEvent(STABLE_VAULT.CLAIM_SUCCESSFUL, {
                  token_id: tokenId,
                  amount: parseFloat(formatUnits(amount || 0n, 18)),
                  hash,
                });

                // Invalidate queries to refresh balances and claims
                queryClient?.invalidateQueries({
                  queryKey: ["tokenBalance"],
                });
                queryClient?.invalidateQueries({
                  queryKey: ["stableVaultClaims"],
                });

                dispatch(
                  updateStakeData({
                    isTransactionProcessing: false,
                  })
                );
              }
            })
            .catch((error) => {
              console.error("Error waiting for claim confirmation:", error);
              dispatch(
                updateStakeData({
                  isTransactionProcessing: false,
                })
              );
            });
        },
      }
    );
  };

  const handleCryptaniumVaultClaim = ({ receiver, tokenId, amount }: any) => {
    if (!address) {
      console.error("No address available for claim");
      return;
    }

    if (!cryptaniumVaultContractConfig[chain.id]) {
      toast({
        description: "Cryptanium vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const vaultConfig =
      cryptaniumVaultContractConfig[chain.id]?.interactionPool;

    if (!vaultConfig) {
      toast({
        description: "Vault configuration not found",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    // Set loading state
    dispatch(updateStakeData({ isTransactionProcessing: true }));

    writeContract(
      {
        ...vaultConfig,
        functionName: "fulfillRedeem",
        args: [receiver],
      },
      {
        onError: (error: any) => {
          const userRejected = isErrorRejectedByUser(error);
          const errorMsg =
            error?.shortMessage || error?.error || error?.message;

          handleErrror(errorMsg, userRejected, "Claim failed");

          trackEvent(CRYPTANIUM_VAULT.CLAIM_FAILED, {
            errorReason: userRejected ? "user rejected" : errorMsg,
            token_id: tokenId,
            amount: parseFloat(formatUnits(amount || 0n, 18)),
          });

          dispatch(
            updateStakeData({
              isTransactionProcessing: false,
            })
          );
        },
        onSuccess: (hash) => {
          publicClient
            ?.waitForTransactionReceipt({ hash })
            .then((confirmation) => {
              if (confirmation.blockHash) {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Claim successful!
                    </Text>
                  ),
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });

                trackEvent(CRYPTANIUM_VAULT.CLAIM_SUCCESSFUL, {
                  token_id: tokenId,
                  amount: parseFloat(formatUnits(amount || 0n, 18)),
                  hash,
                });

                // Invalidate queries to refresh balances and claims
                queryClient?.invalidateQueries({
                  queryKey: ["tokenBalance"],
                });
                queryClient?.invalidateQueries({
                  queryKey: ["cryptaniumVaultClaims"],
                });

                dispatch(
                  updateStakeData({
                    isTransactionProcessing: false,
                  })
                );
              }
            })
            .catch((error) => {
              console.error("Error waiting for claim confirmation:", error);
              dispatch(
                updateStakeData({
                  isTransactionProcessing: false,
                })
              );
            });
        },
      }
    );
  };

  const handleStableVaultRedeemAsset = ({
    amount,
    amountOut,
    outputToken = "USDT",
    shares,
    receiver,
    controller,
  }: any) => {
    if (!address) {
      console.error("No address available for redeem");
      return;
    }

    if (!stableVaultContractConfig[chain.id]) {
      toast({
        description: "Stable vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;

    if (!vaultConfig) {
      toast({
        description: "Vault configuration not found",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const outputTokenConfig =
      stableVaultContractConfig[chain.id]?.tokens?.[outputToken] ||
      config.tokens[outputToken];

    if (!outputTokenConfig) {
      toast({
        description: `${outputToken} token configuration not found`,
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const decimals = outputTokenConfig?.decimals || 18;
    const shareAmount = shares ? formatUnits(shares, decimals) : amount;
    const displayAmount = shareAmount || "0";

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: true,
        isStableVault: true,
        isClaim: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: stableVaultContractConfig[chain.id].xToken.symbol,
        to: outputToken,
        amountIn: displayAmount,
        amountOut: amountOut || displayAmount,
      },
      status: TransactionType.DEPOSIT,
      type: TransactionType.DEPOSIT,
    });

    dispatch(updateStakeData({ isTransactionProcessing: true }));

    transactionParams.current = {
      amount,
      amountOut,
      outputToken,
      shares,
      receiver,
      controller,
    };

    const redeemArgs = [
      outputTokenConfig.address,
      shares || parseUnits(amount, decimals),
      receiver || address,
      controller || address,
    ];

    trackEvent(STABLE_VAULT.CLAIM_INITIATED, {
      amount: parseFloat(
        formatUnits(shares || parseUnits(amount, decimals), decimals)
      ),
    });

    writeContract(
      {
        ...vaultConfig,
        functionName: "redeemAsset",
        args: redeemArgs,
      },
      {
        onError: (error: any) => {
          const userRejected = isErrorRejectedByUser(error);
          const errorMsg =
            error?.shortMessage || error?.error || error?.message;

          setTransaction((prevTransaction) => ({
            ...prevTransaction,
            inProgress: false,
            error: {
              approval: null,
              deposit: {
                hasUserDenied: userRejected,
                promptError: userRejected
                  ? "Transaction rejected by user"
                  : errorMsg,
              },
            },
          }));

          trackEvent(STABLE_VAULT.CLAIM_FAILED, {
            Dapp: "stable_vault",
            txn_hash: null,
            chain: String(chain?.id) || "",
            errorReason: userRejected ? "user rejected" : errorMsg,
            amount: parseFloat(
              formatUnits(shares || parseUnits(amount, decimals), decimals)
            ),
          });

          dispatch(
            updateStakeData({
              isTransactionProcessing: false,
            })
          );
        },
        onSuccess: (hash) => {
          publicClient
            ?.waitForTransactionReceipt({ hash })
            .then((confirmation) => {
              if (confirmation.blockHash) {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Withdrawal successful!
                    </Text>
                  ),
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });

                setTransaction((prevTransaction) => ({
                  ...prevTransaction,
                  inProgress: false,
                  complete: true,
                  receipt: confirmation,
                }));

                trackEvent(STABLE_VAULT.CLAIM_SUCCESSFUL, {
                  amount: parseFloat(
                    formatUnits(
                      shares || parseUnits(amount, decimals),
                      decimals
                    )
                  ),
                  hash,
                });

                // Track generic claim successful
                trackEvent(CLAIM_SUCCESSFUL, {
                  Dapp: "stable_vault",
                  txn_hash: hash,
                  chain: String(chain?.id) || "",
                });

                queryClient?.invalidateQueries({
                  queryKey: ["tokenBalance"],
                });
                queryClient?.invalidateQueries({
                  queryKey: ["stableVaultClaims"],
                });

                dispatch(
                  updateStakeData({
                    isTransactionProcessing: false,
                  })
                );

                dispatch(updateUserData({ stableFormResetNonce: Date.now() }));
              }
            })
            .catch(() => {
              setTransaction((prevTransaction) => ({
                ...prevTransaction,
                inProgress: false,
                error: {
                  approval: null,
                  deposit: {
                    hasUserDenied: false,
                    promptError: "Failed to confirm transaction",
                  },
                },
              }));

              dispatch(
                updateStakeData({
                  isTransactionProcessing: false,
                })
              );
            });
        },
      }
    );
  };

  const handleCryptaniumVaultRedeemAsset = ({
    amount,
    outputToken = "USDT",
    shares,
    receiver,
    controller,
  }: any) => {
    if (!address) {
      console.error("No address available for redeem");
      return;
    }

    if (!cryptaniumVaultContractConfig[chain.id]) {
      toast({
        description: "Cryptanium vault not supported on this network",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const vaultConfig =
      cryptaniumVaultContractConfig[chain.id]?.interactionPool;

    if (!vaultConfig) {
      toast({
        description: "Cryptanium vault configuration not found",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const outputTokenConfig =
      cryptaniumVaultContractConfig[chain.id]?.tokens?.[outputToken] ||
      config.tokens[outputToken];

    if (!outputTokenConfig) {
      toast({
        description: `${outputToken} token configuration not found`,
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    const decimals = outputTokenConfig?.decimals || 18;

    const shareAmount = shares ? formatUnits(shares, decimals) : amount;
    const displayAmount = shareAmount || "0";

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: true,
        isCryptaniumVault: true,
        isClaim: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: cryptaniumVaultContractConfig[chain.id].xToken.symbol,
        to: outputToken,
        amountIn: displayAmount,
        amountOut: displayAmount,
      },
      status: TransactionType.DEPOSIT,
      type: TransactionType.DEPOSIT,
    });

    dispatch(updateStakeData({ isTransactionProcessing: true }));

    transactionParams.current = {
      amount,
      outputToken,
      shares,
      receiver,
      controller,
    };

    const redeemArgs = [
      outputTokenConfig.address,
      shares || parseUnits(amount, decimals),
      receiver || address,
      controller || address,
    ];

    writeContract(
      {
        ...vaultConfig,
        functionName: "redeemAsset",
        args: redeemArgs,
      },
      {
        onError: (error: any) => {
          const userRejected = isErrorRejectedByUser(error);
          const errorMsg =
            error?.shortMessage || error?.error || error?.message;

          setTransaction((prevTransaction) => ({
            ...prevTransaction,
            inProgress: false,
            error: {
              approval: null,
              deposit: {
                hasUserDenied: userRejected,
                promptError: userRejected
                  ? "Transaction rejected by user"
                  : errorMsg,
              },
            },
          }));

          trackEvent(CRYPTANIUM_VAULT.CLAIM_FAILED, {
            errorReason: userRejected ? "user rejected" : errorMsg,
            amount: parseFloat(
              formatUnits(shares || parseUnits(amount, decimals), decimals)
            ),
          });

          dispatch(
            updateStakeData({
              isTransactionProcessing: false,
            })
          );
        },
        onSuccess: (hash) => {
          publicClient
            ?.waitForTransactionReceipt({ hash })
            .then((confirmation) => {
              if (confirmation.blockHash) {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Withdrawal successful!
                    </Text>
                  ),
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });

                setTransaction((prevTransaction) => ({
                  ...prevTransaction,
                  inProgress: false,
                  complete: true,
                  receipt: confirmation,
                }));

                trackEvent(CRYPTANIUM_VAULT.CLAIM_SUCCESSFUL, {
                  amount: parseFloat(
                    formatUnits(
                      shares || parseUnits(amount, decimals),
                      decimals
                    )
                  ),
                  hash,
                });

                queryClient?.invalidateQueries({
                  queryKey: ["tokenBalance"],
                });
                queryClient?.invalidateQueries({
                  queryKey: ["cryptaniumVaultClaims"],
                });

                dispatch(
                  updateStakeData({
                    isTransactionProcessing: false,
                  })
                );

                dispatch(
                  updateUserData({ cryptaniumFormResetNonce: Date.now() })
                );
              }
            })
            .catch(() => {
              setTransaction((prevTransaction) => ({
                ...prevTransaction,
                inProgress: false,
                error: {
                  approval: null,
                  deposit: {
                    hasUserDenied: false,
                    promptError: "Failed to confirm transaction",
                  },
                },
              }));

              dispatch(
                updateStakeData({
                  isTransactionProcessing: false,
                })
              );
            });
        },
      }
    );
  };

  const handleStableVaultApprove = ({
    amount = APPROVAL_AMOUNT,
    amountOut,
  }: any) => {
    if (!address || !signer) return;

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: false,
        isStableVault: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: selectedERC20StableVault,
        to: stableVaultContractConfig[chain.id].xToken.symbol,
        amountIn: amount || "0",
        amountOut: amountOut || amount || "0",
      },
    });

    let token =
      stableVaultContractConfig[chain.id]?.tokens?.[selectedERC20StableVault];

    if (!token) {
      token = config.tokens[selectedERC20StableVault];
    }

    const vaultConfig = stableVaultContractConfig[chain.id]?.interactionPool;

    if (!token || !vaultConfig) {
      console.error("Token or vault config not found for stable vault approve");
      return;
    }

    const checkAllowanceAndApprove = async () => {
      try {
        const currentAllowance = await publicClient?.readContract({
          address: token.address as `0x${string}`,
          abi: token.abi,
          functionName: "allowance",
          args: [address as `0x${string}`, vaultConfig.address],
        });

        const currentAllowanceFormatted = currentAllowance
          ? formatUnits(currentAllowance as bigint, token.decimals)
          : "0";

        const requestedAmount = parseFloat(amount);
        const currentAmount = parseFloat(currentAllowanceFormatted);

        const missingAmount = Math.max(0, requestedAmount - currentAmount);

        if (missingAmount <= 0) {
          toast({
            description: (
              <Text fontWeight={600} fontSize="16px">
                Sufficient allowance available, proceeding to deposit
              </Text>
            ),
            status: "success",
            duration: 3000,
            position: "top",
            isClosable: false,
          });

          setTransaction((transaction) => ({
            ...transaction,
            receipt: null,
            status: TransactionType.DEPOSIT,
            inProgress: false,
          }));

          setTimeout(() => {
            handleStableVaultDeposit({ amount, amountOut, referralId });
          }, 1000);
          return;
        }

        const approvalAmount = maxUint256;
        const displayAmount = parseFloat(APPROVAL_AMOUNT);

        if (selectedERC20StableVault === "USDT" && currentAmount > 0) {
          approveUSDTWithReset(approvalAmount, displayAmount);
        } else {
          approveToken(approvalAmount, displayAmount);
        }
      } catch (error) {
        console.error("Error checking allowance:", error);
        const fullApprovalAmount = maxUint256;
        approveToken(fullApprovalAmount, parseFloat(APPROVAL_AMOUNT));
      }
    };

    const approveUSDTWithReset = (
      approvalAmount: bigint,
      displayAmount: number
    ) => {
      // Encode the approve function call manually for USDT reset
      const spenderPadded = vaultConfig.address.slice(2).padStart(64, "0");
      const amountPadded = "0".padStart(64, "0");
      const resetData = `0x095ea7b3${spenderPadded}${amountPadded}`;

      sendTransaction(
        {
          to: token.address,
          data: resetData as `0x${string}`,
          gas: 100000n,
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;
            handleErrror(errorMsg, userRejected, "USDT allowance reset failed");
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (resetHash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash: resetHash })
              .then(() => {
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      USDT allowance reset complete, setting new approval...
                    </Text>
                  ),
                  status: "info",
                  duration: 2000,
                  position: "top",
                  isClosable: false,
                });

                setTimeout(() => {
                  approveToken(approvalAmount, displayAmount);
                }, 1000);
              })
              .catch((error) => {
                console.error("USDT reset confirmation failed:", error);
                handleErrror("USDT reset confirmation failed", false);
                setTransaction((transaction) => ({
                  ...transaction,
                  inProgress: false,
                  error: {
                    approval: {
                      hasUserDenied: false,
                      promptError: "USDT reset confirmation failed",
                    },
                    deposit: null,
                  },
                }));
              });
          },
        }
      );
    };

    const approveToken = (approvalAmount: bigint, displayAmount: number) => {
      // Special handling for USDT - use sendTransaction because USDT returns no data
      if (selectedERC20StableVault === "USDT") {
        const spenderPadded = vaultConfig.address.slice(2).padStart(64, "0");
        const amountPadded = approvalAmount.toString(16).padStart(64, "0");
        const approveData = `0x095ea7b3${spenderPadded}${amountPadded}`;

        sendTransaction(
          {
            to: token.address,
            data: approveData as `0x${string}`,
            gas: 100000n,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);
              const errorMsg =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMsg, userRejected, "USDT approval failed");

              trackEvent(TOKEN_APPROVE_FAILED, {
                errorReason: userRejected ? "user rejected" : errorMsg,
                token_symbol: token.symbol,
                token_amount: displayAmount,
              });

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                error: {
                  approval: {
                    hasUserDenied: userRejected,
                    promptError: errorMsg,
                  },
                  deposit: null,
                },
              }));
              trackEvent(TOKEN_APPROVE_FAILED, {
                errorReason: userRejected ? "user rejected" : errorMsg,
                token_symbol: token.symbol,
                token_amount: displayAmount,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation) => {
                  if (confirmation.blockHash) {
                    toast({
                      description: (
                        <Text fontWeight={600} fontSize="16px">
                          Asset approved, proceeding to deposit
                        </Text>
                      ),
                      status: "success",
                      duration: 5000,
                      position: "top",
                      isClosable: false,
                    });

                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: null,
                      status: TransactionType.DEPOSIT,
                      inProgress: false,
                    }));

                    trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                      token_symbol: token.symbol,
                      token_amount: displayAmount,
                    });

                    // Invalidate wagmi readContracts queries (for allowance hooks)
                    queryClient?.invalidateQueries({
                      queryKey: ["readContracts"],
                    });
                    queryClient?.invalidateQueries({
                      queryKey: ["erc20Allowance"],
                    });
                    queryClient?.invalidateQueries({
                      queryKey: ["tokenBalance"],
                    });

                    setTimeout(() => {
                      handleStableVaultDeposit({
                        amount,
                        amountOut,
                        referralId,
                      });
                    }, 2000);
                  }
                });
            },
          }
        );

        return; // Exit early for USDT
      }

      // Standard ERC20 approval for non-USDT tokens
      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig.address, approvalAmount],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Approval failed");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: displayAmount,
            });

            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceeding to deposit
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token.symbol,
                    token_amount: displayAmount,
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleStableVaultDeposit({ amount, amountOut, referralId });
                  }, 2000);
                }
              });
          },
        }
      );
    };

    checkAllowanceAndApprove();
  };

  const handleCryptaniumVaultApprove = ({ amount = APPROVAL_AMOUNT }) => {
    if (!address || !signer) return;

    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: true,
        isUnstake: false,
        isCryptaniumVault: true,
      },
      visible: true,
      inProgress: true,
      transaction: {
        from: selectedERC20Cryptanium,
        to: cryptaniumVaultContractConfig[chain.id].xToken.symbol,
        amountIn: amount || "0",
        amountOut: amount || "0", // For cryptanium vault, 1:1 ratio
      },
    });

    let token =
      cryptaniumVaultContractConfig[chain.id]?.tokens?.[
        selectedERC20Cryptanium
      ];

    if (!token) {
      token = config.tokens[selectedERC20Cryptanium];
    }

    const vaultConfig =
      cryptaniumVaultContractConfig[chain.id]?.interactionPool;

    if (!token || !vaultConfig) {
      console.error(
        "Token or vault config not found for cryptanium vault approve"
      );
      return;
    }

    const checkAllowanceAndApprove = async () => {
      try {
        const currentAllowance = await publicClient?.readContract({
          address: token.address as `0x${string}`,
          abi: token.abi,
          functionName: "allowance",
          args: [address as `0x${string}`, vaultConfig.address],
        });

        const currentAllowanceFormatted = currentAllowance
          ? formatUnits(currentAllowance as bigint, token.decimals)
          : "0";

        const requestedAmount = parseFloat(amount);
        const currentAmount = parseFloat(currentAllowanceFormatted);

        const missingAmount = Math.max(0, requestedAmount - currentAmount);

        if (missingAmount <= 0) {
          toast({
            description: (
              <Text fontWeight={600} fontSize="16px">
                Sufficient allowance available, proceeding to deposit
              </Text>
            ),
            status: "success",
            duration: 3000,
            position: "top",
            isClosable: false,
          });

          setTransaction((transaction) => ({
            ...transaction,
            receipt: null,
            status: TransactionType.DEPOSIT,
            inProgress: false,
          }));

          setTimeout(() => {
            handleCryptaniumVaultDeposit({ amount, referralId });
          }, 1000);
          return;
        }

        const approvalAmount = parseUnits(APPROVAL_AMOUNT, token.decimals);
        const displayAmount = parseFloat(APPROVAL_AMOUNT);

        if (selectedERC20Cryptanium === "USDT" && currentAmount > 0) {
          approveUSDTWithReset(approvalAmount, displayAmount);
        } else {
          approveToken(approvalAmount, displayAmount);
        }
      } catch (error) {
        console.error("Error checking allowance:", error);
        const fullApprovalAmount = parseUnits(APPROVAL_AMOUNT, token.decimals);
        approveToken(fullApprovalAmount, parseFloat(APPROVAL_AMOUNT));
      }
    };

    const approveUSDTWithReset = (
      approvalAmount: bigint,
      displayAmount: number
    ) => {
      // Encode the approve function call manually for USDT reset
      // approve(address,uint256) = 0x095ea7b3, amount = 0
      const spenderPadded = vaultConfig.address.slice(2).padStart(64, "0");
      const amountPadded = "0".padStart(64, "0");
      const resetData = `0x095ea7b3${spenderPadded}${amountPadded}`;

      sendTransaction(
        {
          to: token.address,
          data: resetData as `0x${string}`,
          gas: 100000n,
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;
            handleErrror(errorMsg, userRejected, "USDT allowance reset failed");
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (resetHash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash: resetHash })
              .then(() => {
                // Show intermediate toast
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      USDT allowance reset complete, setting new approval...
                    </Text>
                  ),
                  status: "info",
                  duration: 2000,
                  position: "top",
                  isClosable: false,
                });

                // After reset confirmed, approve the needed amount
                setTimeout(() => {
                  approveToken(approvalAmount, displayAmount);
                }, 1000);
              })
              .catch((error) => {
                console.error("USDT reset confirmation failed:", error);
                handleErrror("USDT reset confirmation failed", false);
                setTransaction((transaction) => ({
                  ...transaction,
                  inProgress: false,
                  error: {
                    approval: {
                      hasUserDenied: false,
                      promptError: "USDT reset confirmation failed",
                    },
                    deposit: null,
                  },
                }));
              });
          },
        }
      );
    };

    const approveToken = (approvalAmount: bigint, displayAmount: number) => {
      // Special handling for USDT - use sendTransaction because USDT returns no data
      if (selectedERC20Cryptanium === "USDT") {
        // Encode the approve function call manually for USDT
        // approve(address,uint256) = 0x095ea7b3
        const spenderPadded = vaultConfig.address.slice(2).padStart(64, "0");
        const amountPadded = approvalAmount.toString(16).padStart(64, "0");
        const approveData = `0x095ea7b3${spenderPadded}${amountPadded}`;

        sendTransaction(
          {
            to: token.address,
            data: approveData as `0x${string}`,
            gas: 100000n,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);
              const errorMsg =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMsg, userRejected, "USDT approval failed");

              trackEvent(TOKEN_APPROVE_FAILED, {
                errorReason: userRejected ? "user rejected" : errorMsg,
                token_symbol: token.symbol,
                token_amount: displayAmount,
              });

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                error: {
                  approval: {
                    hasUserDenied: userRejected,
                    promptError: errorMsg,
                  },
                  deposit: null,
                },
              }));
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation) => {
                  if (confirmation.blockHash) {
                    toast({
                      description: (
                        <Text fontWeight={600} fontSize="16px">
                          Asset approved, proceeding to deposit
                        </Text>
                      ),
                      status: "success",
                      duration: 5000,
                      position: "top",
                      isClosable: false,
                    });

                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: null,
                      status: TransactionType.DEPOSIT,
                      inProgress: false,
                    }));

                    trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                      token_symbol: token.symbol,
                      token_amount: displayAmount,
                    });

                    // Invalidate wagmi readContracts queries (for allowance hooks)
                    queryClient?.invalidateQueries({
                      queryKey: ["readContracts"],
                    });
                    queryClient?.invalidateQueries({
                      queryKey: ["erc20Allowance"],
                    });
                    queryClient?.invalidateQueries({
                      queryKey: ["tokenBalance"],
                    });

                    setTimeout(() => {
                      handleCryptaniumVaultDeposit({ amount, referralId });
                    }, 2000);
                  }
                });
            },
          }
        );

        return; // Exit early for USDT
      }

      // Standard ERC20 approval for non-USDT tokens

      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [vaultConfig.address, approvalAmount],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);
            const errorMsg =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMsg, userRejected, "Approval failed");

            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMsg,
              token_symbol: token.symbol,
              token_amount: displayAmount,
            });

            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMsg,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceeding to deposit
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });

                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token.symbol,
                    token_amount: displayAmount,
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    handleCryptaniumVaultDeposit({ amount, referralId });
                  }, 2000);
                }
              });
          },
        }
      );
    };

    checkAllowanceAndApprove();
  };

  const handleApproveToken = (
    forUnstake = false,
    { amount = APPROVAL_AMOUNT, referralId = "" }
  ) => {
    const exchangeConversion = forUnstake ? exchangeRate : 1 / exchangeRate;
    setTransaction({
      ...initialTransaction,
      flags: {
        isVault: false,
        isUnstake: forUnstake,
      },

      visible: true,
      inProgress: true,

      transaction: {
        from: forUnstake ? config.xtoken.symbol : selectedERC20,
        to: forUnstake ? selectedERC20 : config.xtoken.symbol,
        amountIn: amount || "0",
        amountOut: toFixedWithoutRounding(
          (Number(amount) * exchangeConversion).toString(),
          4
        ),
      },
    });

    if (address && signer) {
      dispatch(
        updateStakeData({
          approveTokenLoading: true,
          isTransactionProcessing: true,
        })
      );

      const token = forUnstake ? config.xtoken : config.tokens[selectedERC20];

      writeContract(
        {
          ...token,
          functionName: "approve",
          args: [
            config.interactionPools[
              forUnstake ? "lrtWithdrawalManager" : "lrtDepositPool"
            ].address,
            parseEther(amount),
          ],
        },
        {
          onError: (error: any) => {
            const userRejected = isErrorRejectedByUser(error);

            const errorMessage =
              error?.shortMessage || error?.error || error?.message;

            handleErrror(errorMessage, userRejected, "Approval denied");
            trackEvent(TOKEN_APPROVE_FAILED, {
              errorReason: userRejected ? "user rejected" : errorMessage,
              token_symbol: token.symbol,
              token_amount: parseFloat(amount),
            });
            setTransaction((transaction) => ({
              ...transaction,
              inProgress: false,
              error: {
                approval: {
                  hasUserDenied: userRejected,
                  promptError: errorMessage,
                },
                deposit: null,
              },
            }));
          },
          onSuccess: (hash) => {
            publicClient
              ?.waitForTransactionReceipt({ hash })
              .then((confirmation) => {
                if (confirmation.blockHash) {
                  toast({
                    description: (
                      <Text fontWeight={600} fontSize="16px">
                        Asset approved, proceed to{" "}
                        {forUnstake ? "unstake" : "restake"}
                      </Text>
                    ),
                    status: "success",
                    duration: 5000,
                    position: "top",
                    isClosable: false,
                  });
                  setTransaction((transaction) => ({
                    ...transaction,
                    receipt: null,
                    status: TransactionType.DEPOSIT,
                    inProgress: false,
                  }));

                  trackEvent(TOKEN_APPROVE_SUCCESSFUL, {
                    token_symbol: token,
                    token_amount: parseFloat(amount),
                  });

                  // Invalidate wagmi readContracts queries (for allowance hooks)
                  queryClient?.invalidateQueries({
                    queryKey: ["readContracts"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["erc20Allowance"],
                  });
                  queryClient?.invalidateQueries({
                    queryKey: ["tokenBalance"],
                  });

                  setTimeout(() => {
                    if (forUnstake) {
                      handleUnstake({ amount });
                    } else {
                      handleStake({ amount, referralId });
                    }
                  }, 2000);
                }
              });
          },
        }
      );
    }
  };

  const isApprovalTransaction = (transactionType: string) => {
    return [TXN_TYPES.ERC20_APPROVE, TXN_TYPES.XTOKEN_APPROVE].includes(
      transactionType
    );
  };

  const updateTransactionDetailsWithResults = useCallback(
    (status: string) => {
      dispatch(
        updateStakeData({
          isTransactionProcessing: false,
        })
      );
      setTxnDetail({
        ...txnDetail,
        status,
      });
    },
    [txnDetail]
  );

  const getAssset = useCallback(
    (type: string) => {
      if (type && type.toLowerCase().includes("vault"))
        return selectedERC20Vault;
      return selectedERC20;
    },
    [selectedERC20Vault, selectedERC20]
  );

  const getStakeUnstakeFirebaseMethodName = useCallback(
    (isSuccessful: boolean, type: string) => {
      switch (type) {
        case TXN_TYPES.UNSTAKE:
          return isSuccessful ? UNSTAKE_SUCCESSFUL : UNSTAKE_FAILED;
        case TXN_TYPES.STAKE:
          return isSuccessful ? STAKE_SUCCESSFUL : STAKE_FAILED;
        case TXN_TYPES.ERC20_APPROVE:
          return isSuccessful ? TOKEN_APPROVE_SUCCESSFUL : TOKEN_APPROVE_FAILED;
        case TXN_TYPES.XTOKEN_APPROVE:
          return isSuccessful ? TOKEN_APPROVE_SUCCESSFUL : TOKEN_APPROVE_FAILED;
        case TXN_TYPES.VAULT_DEPOSIT:
          return isSuccessful ? VAULT_DEPOSIT_SUCCESSFUL : VAULT_DEPOSIT_FAILED;
        case TXN_TYPES.VAULT_WITHDRAW:
          return isSuccessful
            ? VAULT_WITHDRAW_SUCCESSFUL
            : VAULT_WITHDRAW_FAILED;
        case TXN_TYPES.VAULT_CLAIM:
          return isSuccessful ? VAULT_CLAIM_SUCCESSFUL : VAULT_CLAIM_FAILED;
        case TXN_TYPES.CLAIM:
          return isSuccessful ? CLAIM_SUCCESSFUL : CLAIM_FAILED;
      }
      return "";
    },
    []
  );

  const logStakeUnstakeEvent = useCallback(
    (isSuccessful: boolean, type: string, hash: string = "") => {
      const amount = txnDetail.amount;

      trackEvent(getStakeUnstakeFirebaseMethodName(isSuccessful, type), {
        amount: parseFloat(Number(amount).toString()),
        hash: hash,
        asset: getAssset(type),
      });
    },
    [getStakeUnstakeFirebaseMethodName, txnDetail.amount]
  );

  useEffect(() => {
    if (
      txnDetail.original &&
      txnDetail.status === TXN_STATUSES.PENDING &&
      !transactionWaitRef.current
    ) {
      transactionWaitRef.current = true;
      txnDetail.original
        .wait(1)
        .then((confirmation: any) => {
          if (confirmation) {
            const type = txnDetail.type;
            const isVault = txnDetail?.original?.isVault ?? false;
            const hash = txnDetail.hash;
            if (confirmation.blockNumber && confirmation.status) {
              updateTransactionDetailsWithResults(TXN_STATUSES.SUCCESS);
              logStakeUnstakeEvent(true, type, hash);

              if (isApprovalTransaction(type)) {
                resetTxns();
                const _type =
                  TXN_TYPES.ERC20_APPROVE === type && isVault
                    ? "deposit"
                    : TXN_TYPES.ERC20_APPROVE === type
                    ? "restaking"
                    : TXN_TYPES.YIELD_VAULT_XTOKEN_APPROVE
                    ? "withdraw"
                    : "unstaking";
                toast({
                  description: (
                    <Text fontWeight={600} fontSize="16px">
                      Asset approved, proceed to {_type}
                    </Text>
                  ),
                  status: "success",
                  // icon: <Icon Icon={SuccessCheck} width="24px" height="24px" />,
                  duration: 5000,
                  position: "top",
                  isClosable: false,
                });
              }
              if (!txnLoader && !isApprovalTransaction(type)) {
                toast({
                  description: `${type} Transaction Successful`,
                  status: "success",
                  duration: 5000,
                  position: "top",
                });
              }
            } else {
              // logStakeUnstakeEvent(false, type, hash, "");
              updateTransactionDetailsWithResults(TXN_STATUSES.ERROR);
              if (!txnLoader && !isApprovalTransaction(type)) {
                toast({
                  description: `${type} Transaction failed`,
                  status: "error",
                  duration: 5000,
                  position: "top",
                });
              }
            }
          }
        })
        .catch((error: any) => {
          dispatch(
            updateStakeData({
              isTransactionProcessing: false,
            })
          );
          setTxnError(error?.shortMessage || error?.error || error?.message);
        });
    }
  }, [
    dispatch,
    resetTxns,
    setTxnError,
    toast,
    txnDetail.hash,
    txnDetail.original,
    txnDetail.status,
    txnDetail.type,
    txnLoader,
    updateTransactionDetailsWithResults,
  ]);

  const openWalletModal = () => {
    dispatch(
      updateModalData({
        modalType: MODAL_TYPES.CONNECT_WALLET,
        isMobileView: isMobileOrTablet ? true : false,
      })
    );
  };

  const handleVaultWithdraw = async (data: any) => {
    const { amount } = data;

    setTransaction((transaction) => ({
      ...transaction,
      inProgress: true,
    }));

    if (address && signer) {
      let params = {
        functionName: "requestRedeem",
        args: [parseEther(amount), address, address],
      };

      try {
        writeContract(
          {
            ...vaultsContractConfig[chain.id].interactionPool,
            ...params,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);

              const errorMessage =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(VAULT_WITHDRAW_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Withdrawal Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(VAULT_WITHDRAW_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        handleErrror(errorMsg, isErrorRejectedByUser(error));
      }
    }
  };

  const handleYieldVaultWithdraw = async (data: any) => {
    const { amount } = data;
    const vaultConfig = yieldVaultContractConfig[chain.id] || {};

    setTransaction((transaction) => ({
      ...transaction,
      inProgress: true,
    }));

    if (address && signer && vaultConfig) {
      // dispatch(updateStakeData({ isTransactionProcessing: true }));
      const vaultAddr = vaultConfig?.interactionPool?.address;

      let params = {
        functionName: "withdraw",
        args: [vaultAddr, parseEther(amount), referralId],
      };
      // const token = yieldVaultContractConfig[chain.id].xToken

      try {
        writeContract(
          {
            ...vaultConfig.gainAdapterETH,
            ...params,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);

              const errorMessage =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(YIELD_VAULT.WITHDRAW_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Withdrawal Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(YIELD_VAULT.WITHDRAW_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        handleErrror(errorMsg, isErrorRejectedByUser(error));
      }
    }
  };

  const handleVaultClaim = async (data: any) => {
    const { year, month, day, amount } = data;
    initiateNewTxn();

    if (address && signer) {
      dispatch(updateStakeData({ isTransactionProcessing: true }));
      let params = {
        functionName: "claim",
        args: [year, month, day, address],
      };

      try {
        writeContract(
          {
            ...vaultsContractConfig[chain.id].interactionPool,
            ...params,
          },
          {
            onError: (error: any) => {
              console.log(error);
              const errorMsg =
                error?.shortMessage || error?.error || error?.message;

              trackEvent(VAULT_CLAIM_FAILED, {
                amount,
                year,
                month,
                day,
                reason: isErrorRejectedByUser(error)
                  ? "user rejected"
                  : errorMsg,
              });
              handleErrror(errorMsg, isErrorRejectedByUser(error));
              dispatch(
                updateStakeData({
                  isTransactionProcessing: false,
                })
              );
            },
            onSuccess: (hash) => {
              const txDetails = {
                hash,
                wait: () => publicClient?.waitForTransactionReceipt({ hash }),
              };

              updateTransactionDetails(
                txDetails as any,
                TXN_TYPES.VAULT_CLAIM,
                amount,
                vaultsContractConfig[chain.id].xToken.symbol
              );
            },
          }
        );
      } catch (error: any) {
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        trackEvent(VAULT_CLAIM_FAILED, {
          amount,
          year,
          month,
          day,
          reason: isErrorRejectedByUser(error) ? "user rejected" : errorMsg,
        });
        handleErrror(errorMsg, isErrorRejectedByUser(error));
        dispatch(
          updateStakeData({
            isTransactionProcessing: false,
          })
        );
      }
    }
  };

  const handleYieldVaultClaim = async (data: any) => {
    const { year, month, day, amount } = data;
    initiateNewTxn();

    if (address && signer) {
      dispatch(updateStakeData({ isTransactionProcessing: true }));
      let params = {
        functionName: "claim",
        args: [year, month, day, address],
      };

      try {
        writeContract(
          {
            ...yieldVaultContractConfig[chain.id].interactionPool,
            ...params,
          },
          {
            onError: (error: any) => {
              console.log(error);
              const errorMsg =
                error?.shortMessage || error?.error || error?.message;

              trackEvent(YIELD_VAULT.CLAIM_FAILED, {
                amount,
                year,
                month,
                day,
                reason: isErrorRejectedByUser(error)
                  ? "user rejected"
                  : errorMsg,
              });
              handleErrror(errorMsg, isErrorRejectedByUser(error));
              // dispatch(
              //   updateStakeData({
              //     isTransactionProcessing: false,
              //   })
              // );
            },
            onSuccess: (hash) => {
              const txDetails = {
                hash,
                wait: () => publicClient?.waitForTransactionReceipt({ hash }),
              };

              // trackEvent(VAULT_CLAIM_SUCCESSFUL, {
              //   amount,
              //   year,
              //   month,
              //   day,
              // });

              updateTransactionDetails(
                txDetails as any,
                TXN_TYPES.VAULT_CLAIM,
                amount,
                yieldVaultContractConfig[chain.id]?.xToken?.symbol
              );
            },
          }
        );
      } catch (error: any) {
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        trackEvent(YIELD_VAULT.CLAIM_FAILED, {
          amount,
          year,
          month,
          day,
          reason: isErrorRejectedByUser(error) ? "user rejected" : errorMsg,
        });
        handleErrror(errorMsg, isErrorRejectedByUser(error));
        dispatch(
          updateStakeData({
            isTransactionProcessing: false,
          })
        );
      }
    }
  };

  const handleVaultDeposit = async (data: any) => {
    const { amount, referralId } = data;

    const VAULT_ERC20 = [ETHX, STETH];

    if (address && signer) {
      setTransaction((transaction) => ({
        ...transaction,
        visible: true,
        status: TransactionType.DEPOSIT,
        inProgress: true,
        flags: { ...initialTransaction.flags, isVault: true },

        error: initialTransaction.error,
      }));
      let params: any = {};

      const isETH = selectedERC20Vault === ETH;

      if (isEthereumChain) {
        if (VAULT_ERC20.includes(selectedERC20Vault)) {
          params = {
            functionName: "getRSETHWithERC20",
            args: [
              config.tokens[selectedERC20Vault].address,
              parseEther(amount?.toString()),
              sha256(referralId),
            ],
          };
        } else if (selectedERC20Vault === "rsETH") {
          params = {
            functionName: "depositRsETH",
            args: [parseEther(amount), sha256(referralId)],
          };
        } else if (selectedERC20Vault === ETH) {
          params = {
            functionName: "getRSETHWithETH",
            args: [sha256(referralId)],
            value: parseEther(amount),
          };
        }
      } else {
        if (isETH) {
          params = {
            functionName: "deposit",
            args: [sha256(referralId)],
            value: parseEther(amount),
          };
        } else {
          params = {
            functionName: "deposit",
            args: [
              config.xtoken.address,
              parseEther(amount),
              sha256(referralId),
            ],
          };
        }
      }

      const vaultConfig = isEthereumChain
        ? vaultsContractConfig[chain.id].rsETHAdapterPool
        : vaultsContractConfig[chain.id].interactionPool;

      try {
        writeContract(
          {
            ...vaultConfig,
            ...params,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);
              const errorMessage =
                error?.shortMessage || error?.error || error?.message;
              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(VAULT_DEPOSIT_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Deposit Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(VAULT_DEPOSIT_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
      }
    }
  };

  const handleYieldVaultDeposit = async (data: any) => {
    const { amount, referralId } = data;

    const vaultConfig = yieldVaultContractConfig[chain.id] || {};

    if (address && signer) {
      setTransaction((transaction) => ({
        ...transaction,
        visible: true,
        status: TransactionType.DEPOSIT,
        inProgress: true,
        flags: {
          ...initialTransaction.flags,
          isVault: true,
          isYieldVault: true,
        },
        error: initialTransaction.error,
      }));
      let params: any = {};

      // if (address && signer && vaultConfig) {
      //   dispatch(updateStakeData({ isTransactionProcessing: true }));
      //   let params: any = {};

      // const token = yieldVaultConfig.tokens[selectedERC20YieldVault];
      const vaultAddr = vaultConfig?.interactionPool?.address;

      const token =
        selectedERC20YieldVault === config?.xtoken?.symbol
          ? config?.xtoken
          : config?.tokens[selectedERC20YieldVault];

      if (selectedERC20YieldVault === ETH) {
        params = {
          functionName: "depositETH",
          args: [vaultAddr, sha256(referralId)],
          value: parseEther(amount),
        };
      } else {
        params = {
          functionName: "depositETHAsset",
          args: [
            token.address,
            parseEther(amount),
            vaultAddr,
            sha256(referralId),
          ],
        };
      }

      try {
        writeContract(
          {
            ...vaultConfig.gainAdapterETH,
            ...params,
          },
          {
            onError: (error: any) => {
              const userRejected = isErrorRejectedByUser(error);
              const errorMessage =
                error?.shortMessage || error?.error || error?.message;
              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(YIELD_VAULT.DEPOSIT_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Deposit Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(YIELD_VAULT.DEPOSIT_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
      }
    }
  };

  // const handleStakeForStaderLedger = async ({ amount, referralId }: any) => {
  //   // Step1 = convert to ETHX
  //   // Step2 = fetch allowance & ETHX balance
  //   // Step3 = If alreaddyy approve jump to step5
  //   // Step4 = If not approved approve allowance
  //   // step5 = ETHX to rsETH

  //   let activeIndex = 0;
  //   setStaderLedgerTxn({
  //     ...staderLedgerTxn,
  //     activeIndex,
  //   });

  //   const params = { from: address, amount, referralId, lstToken: undefined };

  //   const request = await axios.post(
  //     `https://yields.kelpdao.xyz/ethx/stake?clientId=${STADER_CLIENT_ID}`,
  //     {
  //       ...params,
  //       chainId: wagmiChain?.id,
  //     }
  //   );

  //   if (!signer) {
  //     return;
  //   }

  //   const txString = request.data;

  //   const decodedTx = ethers.utils.parseTransaction(
  //     Buffer.from(txString, "hex")
  //   );

  //   const viemTx: SendTransactionParameters = {
  //     nonce: decodedTx.nonce,
  //     // gasPrice: decodedTx.gasPrice,
  //     // maxFeePerGas: decodedTx.gasLimit,
  //     to: decodedTx.to as `0x${string}`,
  //     value: parseEther(amount),
  //     data: decodedTx.data as `0x${string}`,
  //     // chain: mainnet,
  //     chainId: mainnet.id,
  //     gas: undefined,
  //   };

  //   trackEvent(STADER_RESTAKE_INITIATE, {
  //     stake_amount_entered: parseFloat(amount),
  //   });

  //   try {
  //     const hash = await sendTransaction(wagmiConfig, viemTx);
  //     const txReceipt = await publicClient?.waitForTransactionReceipt({
  //       hash,
  //     });

  //     trackEvent(STADER_RESTAKE_COMPLETE, {
  //       stake_amount_entered: parseFloat(amount),
  //       step: "step_1",
  //     });

  //     activeIndex += 1;
  //     setStaderLedgerTxn({
  //       ...staderLedgerTxn,
  //       activeIndex,
  //       hash: txReceipt?.transactionHash || "",
  //     });
  //   } catch (error: any) {
  //     console.log("Error ", error);

  //     const errorMessage = error?.error || error?.message;
  //     trackEvent(STADER_RESTAKE_FAILED, {
  //       stake_amount_entered: parseFloat(amount),
  //       step: "step_1",
  //       reason: isErrorRejectedByUser(error) ? "user rejected" : errorMessage,
  //     });

  //     setStaderLedgerTxn({
  //       ...staderLedgerTxn,
  //       activeIndex,
  //       error: errorMessage || "Error converting to ETHx",
  //       hasUserDenied: isErrorRejectedByUser(error),
  //     });
  //     return;
  //   }

  //   // Step 2

  //   const token = config.tokens[ETHX];

  //   const exchangeRateToEth = await axios.get(
  //     "https://universe.kelpdao.xyz/eth/exchangeRate"
  //   );

  //   const ethxAmount = amount * (1 / exchangeRateToEth.data.value);

  //   const allowanceData = await readContract(wagmiConfig, {
  //     ...token,
  //     functionName: "allowance",
  //     args: [address, config.interactionPools.lrtDepositPool.address],
  //   });

  //   const ethxAllowance = +toFixedWithoutRounding(
  //     allowanceData
  //       ? formatUnits(allowanceData as bigint, config.tokens[ETHX].decimals)
  //       : "0",
  //     8
  //   );

  //   if (
  //     ethxAllowance &&
  //     Number(ethxAllowance) > tokensBalances[ETHX] + ethxAmount
  //   ) {
  //     activeIndex += 1;
  //     setStaderLedgerTxn({
  //       ...staderLedgerTxn,
  //       activeIndex,
  //       hash: "",
  //     });

  //     trackEvent(STADER_RESTAKE_COMPLETE, {
  //       stake_amount_entered: parseFloat(amount),
  //       step: "step_2_skiped",
  //     });
  //   } else {
  //     // Step for approval

  //     try {
  //       const hash = await writeContractAction(wagmiConfig, {
  //         ...token,
  //         functionName: "approve",
  //         args: [
  //           config.interactionPools["lrtDepositPool"].address,
  //           parseEther(APPROVAL_AMOUNT),
  //         ],
  //       });

  //       const txReceipt = await publicClient?.waitForTransactionReceipt({
  //         hash,
  //       });

  //       trackEvent(STADER_RESTAKE_COMPLETE, {
  //         stake_amount_entered: parseFloat(amount),
  //         step: "step_2",
  //       });

  //       activeIndex += 1;
  //       setStaderLedgerTxn({
  //         ...staderLedgerTxn,
  //         activeIndex,
  //         hash: txReceipt?.transactionHash || "",
  //       });
  //     } catch (error: any) {
  //       console.log("Error ", error);

  //       const errorMessage = error?.error || error?.message;

  //       trackEvent(STADER_RESTAKE_FAILED, {
  //         stake_amount_entered: parseFloat(amount),
  //         step: "step_2",
  //         reason: isErrorRejectedByUser(error) ? "user rejected" : errorMessage,
  //       });

  //       setStaderLedgerTxn({
  //         ...staderLedgerTxn,
  //         activeIndex,
  //         error: errorMessage || "Error approving ETHx",
  //         hasUserDenied: isErrorRejectedByUser(error),
  //       });
  //       return;
  //     }
  //   }

  //   // Step 3 convert ETHX to rsETH

  //   try {
  //     const params = {
  //       functionName: "depositAsset",
  //       args: [
  //         config.tokens[ETHX].address,
  //         parseEther(ethxAmount?.toString()),
  //         0,
  //         sha256(referralId),
  //       ],
  //     };

  //     const hash = await writeContractAction(wagmiConfig, {
  //       ...params,
  //       ...config.interactionPools.lrtDepositPool,
  //     });

  //     const txReceipt = await publicClient?.waitForTransactionReceipt({
  //       hash,
  //     });

  //     trackEvent(STADER_RESTAKE_COMPLETE, {
  //       stake_amount_entered: parseFloat(amount),
  //       step: "step_3",
  //     });

  //     setStaderLedgerTxn({
  //       ...staderLedgerTxn,
  //       activeIndex: activeIndex + 1,
  //       hash: txReceipt?.transactionHash || "",
  //     });
  //   } catch (error: any) {
  //     console.log("Error ", error);

  //     const errorMessage = error?.error || error?.message;
  //     trackEvent(STADER_RESTAKE_FAILED, {
  //       stake_amount_entered: parseFloat(amount),
  //       step: "step_3",
  //       reason: isErrorRejectedByUser(error) ? "user rejected" : errorMessage,
  //     });

  //     setStaderLedgerTxn({
  //       ...staderLedgerTxn,
  //       activeIndex,
  //       error: errorMessage || "Error converting rsETH",
  //       hasUserDenied: isErrorRejectedByUser(error),
  //     });
  //   }
  //   return;
  // };

  const handleStake = async (data: any) => {
    if (!isRestakeSupported) {
      // eslint-disable-next-line no-console
      console.log("restake not supported on chain", config.chain.name);
      return;
    }

    if (!wagmiChain) {
      toast({
        description: "invalid chain",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log("invalid chain");
      return;
    }

    if (wagmiChain?.id !== config.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    const { amount, referralId } = data;

    setTransaction((transaction) => ({
      ...transaction,
      visible: true,
      status: TransactionType.DEPOSIT,
      inProgress: true,
      flags: initialTransaction.flags,

      error: initialTransaction.error,
    }));

    if (amount === formatNumber(Number(tokenAmount), false, 4, true)) {
      trackEvent(MAX_STAKE, {
        amount,
      });
    }
    if (address && signer) {
      let params;

      if (isEthereumChain) {
        params =
          selectedERC20 === ETH
            ? {
                functionName: "depositETH",
                args: [0, sha256(referralId)],
                value: parseEther(amount),
              }
            : {
                functionName: "depositAsset",
                args: [
                  config.tokens[selectedERC20].address,
                  parseEther(amount),
                  0,
                  sha256(referralId),
                ],
              };
      } else if (chain.id === arbitrum.id) {
        const isETH = selectedERC20 === ETH;

        if (isETH) {
          params = {
            functionName: "deposit",
            args: [sha256(referralId)],
            value: parseEther(amount),
          };
        } else {
          params = {
            functionName: "deposit",
            args: [
              config.tokens[selectedERC20].address,
              parseEther(amount),
              sha256(referralId),
            ],
          };
        }
      } else if (
        chain.id === xLayer.id ||
        (chain.id === base.id && selectedERC20 !== ETH) ||
        chain.id === tac.id ||
        chain.id === avalanche.id ||
        chain.id === sonic.id ||
        chain.id === plasma.id
      ) {
        params = {
          functionName: "deposit",
          args: [
            config.tokens[selectedERC20].address,
            parseEther(amount),
            sha256(referralId),
          ],
        };
      } else {
        params = {
          functionName: "deposit",
          args: [sha256(referralId)],
          value: parseEther(amount),
        };
      }

      try {
        writeContract(
          {
            ...config.interactionPools.lrtDepositPool,
            ...params,
          },
          {
            onError: (error: any) => {
              console.log(error);
              const userRejected = isErrorRejectedByUser(error);

              const errorMessage =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(STAKE_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Stake Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(STAKE_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
        const errorMsg = error?.shortMessage || error?.error || error?.message;

        handleErrror(errorMsg, isErrorRejectedByUser(error));

        trackEvent(STAKE_FAILED, {
          asset_in: transaction.transaction.from,
          asset_out: transaction.transaction.to,
          asset_in_amt: parseFloat(transaction.transaction.amountIn),
          asset_out_amt: parseFloat(transaction.transaction.amountOut),
          errorReason: isErrorRejectedByUser(error)
            ? "user rejected"
            : errorMsg,
        });
      }
    }
  };

  const handleUnstake = async (data: any) => {
    const { amount } = data;

    if (!isWithdrawSupported) {
      // eslint-disable-next-line no-console
      console.log("unstake not supported on chain", config.chain.name);
      return;
    }

    if (!wagmiChain) {
      toast({
        description: "invalid chain",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log("invalid chain");
      return;
    }

    if (wagmiChain?.id !== config.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    if (amount === formatNumber(Number(tokenAmount), false, 4, true)) {
      trackEvent(MAX_UNSTAKE, {
        amount,
      });
    }

    setTransaction((transaction) => ({
      ...transaction,
      visible: true,
      status: TransactionType.DEPOSIT,
      inProgress: true,
      flags: {
        isUnstake: true,
        isVault: false,
      },
      error: initialTransaction.error,
    }));

    const asset =
      selectedERC20 !== ETH
        ? config.tokens[selectedERC20].address
        : config.nativeToken.address;

    if (address && signer) {
      try {
        writeContract(
          {
            ...config.interactionPools.lrtWithdrawalManager,
            functionName: "initiateWithdrawal",
            args: [asset, parseEther(amount), sha256(referralId)],
          },
          {
            onError: (error: any) => {
              console.log(error);
              const userRejected = isErrorRejectedByUser(error);

              const errorMessage =
                error?.shortMessage || error?.error || error?.message;

              handleErrror(errorMessage, userRejected);

              setTransaction((transaction) => ({
                ...transaction,
                inProgress: false,
                complete: true,
                error: {
                  ...transaction.error,
                  deposit: {
                    hasUserDenied: userRejected,
                    promptError: errorMessage,
                  },
                },
              }));

              trackEvent(UNSTAKE_FAILED, {
                asset_in: transaction.transaction.from,
                asset_out: transaction.transaction.to,
                asset_in_amt: parseFloat(transaction.transaction.amountIn),
                asset_out_amt: parseFloat(transaction.transaction.amountOut),
                errorReason: userRejected ? "user rejected" : errorMessage,
              });
            },
            onSuccess: (hash) => {
              publicClient
                ?.waitForTransactionReceipt({ hash })
                .then((confirmation: TransactionReceipt) => {
                  if (confirmation.status && confirmation.blockNumber) {
                    setTransaction((transaction) => ({
                      ...transaction,
                      receipt: confirmation,
                      inProgress: false,
                      complete: true,
                    }));

                    toast({
                      description: `Unstake Successful`,
                      status: "success",
                      duration: 5000,
                      position: "top",
                    });

                    trackEvent(UNSTAKE_SUCCESSFUL, {
                      asset_in: transaction.transaction.from,
                      asset_out: transaction.transaction.to,
                      asset_in_amt: parseFloat(
                        transaction.transaction.amountIn
                      ),
                      asset_out_amt: parseFloat(
                        transaction.transaction.amountOut
                      ),
                      hash,
                    });
                  }
                });
            },
          }
        );
      } catch (error: any) {
        console.log(error);
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        trackEvent(UNSTAKE_FAILED, {
          asset_in: transaction.transaction.from,
          asset_out: transaction.transaction.to,
          asset_in_amt: parseFloat(transaction.transaction.amountIn),
          asset_out_amt: parseFloat(transaction.transaction.amountOut),
          errorReason: isErrorRejectedByUser(error)
            ? "user rejected"
            : errorMsg,
        });

        handleErrror(errorMsg, isErrorRejectedByUser(error));
      }
    }
  };

  const handleClaim = async (data: any) => {
    if (!isWithdrawSupported) {
      // eslint-disable-next-line no-console
      console.log("claim not supported on chain", config.chain.name);
      return;
    }

    if (!wagmiChain) {
      toast({
        description: "invalid chain",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log("invalid chain");
      return;
    }

    if (wagmiChain?.id !== config.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    const { amount, asset, callback } = data;
    initiateNewTxn();

    if (address && signer) {
      dispatch(
        updateStakeData({ isClaiming: true, isTransactionProcessing: true })
      );

      try {
        writeContract(
          {
            ...config.interactionPools.lrtWithdrawalManager,
            functionName: "completeWithdrawal",
            args: [asset, sha256(referralId)],
          },
          {
            onError: (error: any) => {
              console.log(error);
              const errorMsg =
                error?.shortMessage || error?.error || error?.message;
              callback?.();
              trackEvent(CLAIM_FAILED, {
                amount,
                asset,
                reason: isErrorRejectedByUser(error)
                  ? "user rejected"
                  : errorMsg,
              });
              handleErrror(errorMsg, isErrorRejectedByUser(error));
              dispatch(
                updateStakeData({
                  isClaiming: false,
                  isTransactionProcessing: false,
                })
              );
            },
            onSuccess: (hash) => {
              const txDetails = {
                hash,
                wait: () => publicClient?.waitForTransactionReceipt({ hash }),
              };

              updateTransactionDetails(
                txDetails as any,
                TXN_TYPES.CLAIM,
                amount,
                config.xtoken.symbol
              );
              callback?.();
            },
          }
        );
      } catch (error: any) {
        console.log(error);
        const errorMsg = error?.shortMessage || error?.error || error?.message;
        trackEvent(CLAIM_FAILED, {
          claim_amount: parseFloat(amount),
          reason: isErrorRejectedByUser(error) ? "user rejected" : errorMsg,
        });
        handleErrror(errorMsg, isErrorRejectedByUser(error));
        dispatch(
          updateStakeData({
            isClaiming: false,
            isTransactionProcessing: false,
          })
        );
      }
    }
  };

  const closeTransactionModal = () => {
    setTransaction({ ...initialTransaction });
    setTxnDetail(initialTxn);
    setTxnError(initialTxnError);
    transactionParams.current = null;
  };

  const handleRetryApprove = () => {
    const {
      isVault,
      isYieldVault,
      isCryptaniumVault,
      isStableVault,
      isUnstake,
    } = transaction.flags;
    const params = transactionParams.current;

    const isRetryingDeposit =
      transaction.error.deposit && !transaction.error.approval;

    if (isRetryingDeposit && isVault) {
      if (isYieldVault && isUnstake) {
        return handleYieldVaultWithdraw(params);
      } else if (isCryptaniumVault && isUnstake) {
        return handleCryptaniumVaultWithdraw(params);
      } else if (isStableVault && isUnstake) {
        return handleStableVaultWithdraw(params);
      } else if (isYieldVault && !isUnstake) {
        return handleYieldVaultDeposit(params);
      } else if (isCryptaniumVault && !isUnstake) {
        return handleCryptaniumVaultDeposit(params);
      } else if (isStableVault && !isUnstake) {
        return handleStableVaultDeposit(params);
      } else if (
        !isYieldVault &&
        !isCryptaniumVault &&
        !isStableVault &&
        isUnstake
      ) {
        return handleVaultWithdraw(params);
      } else {
        return handleVaultDeposit(params);
      }
    } else {
      if (isVault) {
        if (isYieldVault && isUnstake)
          return handleYieldVaultXTokenApprove(params);
        if (isCryptaniumVault && isUnstake)
          return handleCryptaniumVaultXTokenApprove(params);
        if (isStableVault && isUnstake)
          return handleStableVaultXTokenApprove(params);
        if (isCryptaniumVault) return handleCryptaniumVaultApprove(params);
        if (isStableVault) return handleStableVaultApprove(params);
        if (isYieldVault) return handleYieldVaultApprove(params);
        return handleVaultApprove(params);
      }

      handleApproveToken(isUnstake, {
        amount: params?.amount,
        referralId: params?.referralId,
      });
    }
  };

  const handleConfirmClick = () => {
    if (transaction.flags.isVault) {
      if (transaction.flags.isUnstake) {
        if (transaction.flags.isYieldVault) {
          handleYieldVaultWithdraw(transactionParams.current);
        } else if (transaction.flags.isCryptaniumVault) {
          if (transaction.flags.isClaim) {
            handleCryptaniumVaultRedeemAsset(transactionParams.current);
          } else {
            handleCryptaniumVaultWithdraw(transactionParams.current);
          }
        } else if (transaction.flags.isStableVault) {
          if (transaction.flags.isClaim) {
            handleStableVaultRedeemAsset(transactionParams.current);
          } else {
            handleStableVaultWithdraw(transactionParams.current);
          }
        } else {
          handleVaultWithdraw(transactionParams.current);
        }
      } else {
        if (transaction.flags.isYieldVault) {
          handleYieldVaultDeposit(transactionParams.current);
        } else if (transaction.flags.isCryptaniumVault) {
          handleCryptaniumVaultDeposit(transactionParams.current);
        } else if (transaction.flags.isStableVault) {
          handleStableVaultDeposit(transactionParams.current);
        } else {
          handleVaultDeposit(transactionParams.current);
        }
      }
    } else {
      if (transaction.flags.isUnstake) {
        handleUnstake(transactionParams.current);
      } else {
        handleStake(transactionParams.current);
      }
    }
  };

  const attachEventListners = (event: any) => {
    switch (event.name) {
      case EVENTS.HANDLE_ERC20_APPROVE: {
        transactionParams.current = event.data;
        handleApproveToken(false, event.data);
        break;
      }
      case EVENTS.HANDLE_VAULT_APPROVE: {
        transactionParams.current = event.data;
        handleVaultApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_YIELD_VAULT_APPROVE: {
        transactionParams.current = event.data;
        handleYieldVaultApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_YIELD_VAULT_XTOKEN_APPROVE: {
        transactionParams.current = event.data;
        handleYieldVaultXTokenApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_APPROVE: {
        transactionParams.current = event.data;
        handleStableVaultApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_DEPOSIT: {
        transactionParams.current = event.data;
        handleStableVaultDeposit(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_WITHDRAW: {
        transactionParams.current = event.data;
        handleStableVaultWithdraw(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_XTOKEN_APPROVE: {
        transactionParams.current = event.data;
        handleStableVaultXTokenApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_CLAIM: {
        handleStableVaultClaim(event.data);
        break;
      }
      case EVENTS.HANDLE_STABLE_VAULT_REDEEM_ASSET: {
        handleStableVaultRedeemAsset(event.data);
        break;
      }
      case EVENTS.HANDLE_CRYPTANIUM_VAULT_APPROVE: {
        transactionParams.current = event.data;
        handleCryptaniumVaultApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_CRYPTANIUM_VAULT_XTOKEN_APPROVE: {
        transactionParams.current = event.data;
        handleCryptaniumVaultXTokenApprove(event.data);
        break;
      }
      case EVENTS.HANDLE_CRYPTANIUM_VAULT_CLAIM: {
        handleCryptaniumVaultClaim(event.data);
        break;
      }
      case EVENTS.HANDLE_CRYPTANIUM_VAULT_REDEEM_ASSET: {
        handleCryptaniumVaultRedeemAsset(event.data);
        break;
      }
      case EVENTS.HANDLE_XTOKEN_APPROVE: {
        handleApproveToken(true, event.data);
        break;
      }
      case EVENTS.HANDLE_VAULT_CLAIM: {
        handleVaultClaim(event.data);
        break;
      }
      case EVENTS.HANDLE_YIELD_VAULT_CLAIM: {
        handleYieldVaultClaim(event.data);
        break;
      }
      case EVENTS.OPEN_WALLET_MODAL: {
        openWalletModal();
        break;
      }
      case EVENTS.SWITCH_NETWORK: {
        switchChain({ chainId: chains[0].id });
        break;
      }
      case EVENTS.HANDLE_STAKE: {
        transactionParams.current = event.data;
        setTransaction({
          ...initialTransaction,
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: selectedERC20,
            to: config.xtoken.symbol,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) * (1 / exchangeRate)).toString(),
              4
            ),
          },
        });
        break;
      }
      case EVENTS.HANDLE_VAULT_DEPOSIT: {
        transactionParams.current = event.data;
        const exchangeRate = getAgEthExchange();
        setTransaction({
          ...initialTransaction,
          flags: { ...initialTransaction.flags, isVault: true },
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: selectedERC20Vault,
            to: vaultsContractConfig[chain.id].xToken.symbol,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) / exchangeRate).toString(),
              4
            ),
          },
        });
        break;
      }
      case EVENTS.HANDLE_YIELD_VAULT_DEPOSIT: {
        transactionParams.current = event.data;
        const exchangeRate = getHgEthExchange();
        setTransaction({
          ...initialTransaction,
          flags: {
            ...initialTransaction.flags,
            isVault: true,
            isYieldVault: true,
          },
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: selectedERC20YieldVault,
            to: yieldVaultContractConfig[chain.id].xToken.symbol,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) / exchangeRate).toString(),
              4
            ),
          },
        });
        break;
      }

      case EVENTS.HANDLE_CRYPTANIUM_VAULT_DEPOSIT: {
        transactionParams.current = event.data;
        const exchangeRate = getXgUSDExchangeRate();
        setTransaction({
          ...initialTransaction,
          flags: {
            ...initialTransaction.flags,
            isVault: true,
            isCryptaniumVault: true,
          },
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: selectedERC20Cryptanium,
            to: cryptaniumVaultContractConfig[chain.id].xToken.symbol,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) / exchangeRate).toString(),
              4
            ),
          },
        });
        break;
      }
      case EVENTS.HANDLE_CRYPTANIUM_VAULT_WITHDRAW: {
        transactionParams.current = event.data;
        const token = cryptaniumVaultContractConfig[chain.id].xToken;
        setTransaction({
          ...initialTransaction,
          flags: { isVault: true, isUnstake: true, isCryptaniumVault: true },
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: token.symbol,
            to: USDT,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (
                Number(event.data.amount) * Number(conversionsDataXgUSD.toERC20)
              ).toString(),
              4
            ),
          },
        });

        break;
      }
      // case EVENTS.HANDLE_VAULT_CLAIM: {
      //   handleVaultClaim(event.data);
      //   break;
      // }
      case EVENTS.HANDLE_VAULT_WITHDRAW: {
        transactionParams.current = event.data;
        const token = vaultsContractConfig[chain.id].xToken;
        setTransaction({
          ...initialTransaction,
          flags: { isVault: true, isUnstake: true },
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: token.symbol,
            to: RSETH,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (
                Number(event.data.amount) * Number(conversionsDataAgEth.toERC20)
              ).toString(),
              4
            ),
          },
        });

        break;
      }
      case EVENTS.HANDLE_YIELD_VAULT_WITHDRAW: {
        transactionParams.current = event.data;
        const token = yieldVaultContractConfig[chain.id].xToken;
        setTransaction({
          ...initialTransaction,
          flags: { isVault: true, isUnstake: true, isYieldVault: true },
          status: TransactionType.DEPOSIT,
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: token.symbol,
            to: RSETH,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (
                Number(event.data.amount) * Number(conversionsDataHgEth.toERC20)
              ).toString(),
              4
            ),
          },
        });
        break;
      }
      case EVENTS.HANDLE_UNSTAKE: {
        const exchangeConversion = exchangeRate;
        setTransaction({
          ...initialTransaction,
          type: TransactionType.DEPOSIT,
          visible: true,
          inProgress: true,
          transaction: {
            from: config.xtoken.symbol,
            to: selectedERC20,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) * exchangeConversion).toString(),
              4
            ),
          },
          flags: {
            isUnstake: true,
            isVault: false,
          },
        });
        transactionParams.current = event.data;
        handleUnstake(transactionParams.current);
        break;
      }
      case EVENTS.HANDLE_CLAIM: {
        handleClaim(event.data);
        break;
      }
      case EVENTS.HANDLE_UNSTAKE_GAIN_OPP: {
        setTransaction({
          ...initialTransaction,
          type: TransactionType.DEPOSIT,
          visible: true,
          transaction: {
            from: config.xtoken.symbol,
            to: selectedERC20,
            amountIn: event.data.amount || "0",
            amountOut: toFixedWithoutRounding(
              (Number(event.data.amount) * exchangeRate).toString(),
              4
            ),
          },
          flags: {
            isUnstake: true,
            isVault: false,
          },
          nextAction: () => {
            attachEventListners({
              name: event.data.nextAction,
              data: event.data,
            });
          },
        });
      }
    }
  };

  useEffect(() => {
    attachEventListners(emitter);
  }, [emitter]);

  useEffect(() => {
    if (address && activeConnector?.id) {
      dispatch(
        updateWalletData({
          walletAddress: address,
          walletName: activeConnector.id,
        })
      );
    }
  }, [activeConnector?.id, address]);

  const handleTxViewOnEtherScan = () => {
    trackEvent(VIEW_ADDRESS_ON_SCANNER, {
      cta_location: "tx_modal",
    });
    const url = `${chain!.blockExplorers?.default.url}/tx/${txnDetail.hash}`;
    window.open(url, "_blank");
  };

  return (
    <>
      {modalType === MODAL_TYPES.CONNECT_WALLET && (
        <WalletConnectModal
          isOpen={
            modalType === MODAL_TYPES.CONNECT_WALLET &&
            !isMobileView &&
            !isConnected
          }
          closeModal={handleCloseModal}
          onConnectWallet={onWalletConnect}
        />
      )}

      <AccountModal
        isOpen={modalType === MODAL_TYPES.WALLET_MODAL && isConnected}
        onCancel={onCloseAccountModal}
        walletAddress={address}
        connectorId={activeConnector?.id || ""}
        scannerLink={`${chain!.blockExplorers?.default.url}/address/${address}`}
      />
      {children}

      {modalType === MODAL_TYPES.WALLET_CONNECT_MESSAGE_MODAL && (
        <WalletMessageModal
          isOpen={modalType === MODAL_TYPES.WALLET_CONNECT_MESSAGE_MODAL}
          closeModal={handleCloseModal}
          error={errorMessage || ""}
          onSubmitPrimary={() => emitReduxEvent(EVENTS.SWITCH_NETWORK)}
        />
      )}

      {staderLedgerTxn.activeIndex >= 0 && (
        <StaderLedgerTxModal
          isOpen={staderLedgerTxn.activeIndex >= 0}
          activeIndex={staderLedgerTxn.activeIndex}
          closeAlert={() => {
            setStaderLedgerTxn(initialStaderLedgerTxn);
          }}
          error={staderLedgerTxn.error}
          hasUserDenied={staderLedgerTxn.hasUserDenied}
        />
      )}

      {txnLoader && (
        <TransactionModal
          isOpen={txnLoader}
          hash={txnDetail.hash}
          isTxnProcessing={txnDetail.status === TXN_STATUSES.PENDING}
          error={txnError.promptError}
          daysToWaitForWithdraw={selectedERC20 === ETH ? "~14-16" : "~7-10"}
          hasUserDenied={txnError.hasUserDenied}
          closeAlert={resetTxns}
          handleTxView={handleTxViewOnEtherScan}
          transactionType={txnDetail.type}
          chainId={chain!.id}
          amount={txnDetail.amount}
        />
      )}
      {transaction.visible && (
        <DepositModalV2
          {...transaction}
          closeAlert={closeTransactionModal}
          retryTransaction={handleRetryApprove}
          handleConfirm={handleConfirmClick}
        />
      )}
    </>
  );
};

export default Services;
